import axios from "axios";
import * as React from "react";
import { apiUrl } from "../../../config/api";
import { ISurveyEvalResponse } from "../../../services";
import { hexToRGB, roundToTen } from "../../../util";
import { WiseLeadershipGridChart } from "../EvaluationPage/WiseLeadershipGridChart";
import reportDesignCss from "../../../report-design";

const COACH_REPORT_LIMITS = {
  overallSummary: 400,
  highlightBody: 500,
  suggestionBody: 650,
  resourceTitle: 180,
  resourceBody: 900,
  resourceWhyChosen: 900,
  parentSummary: 1000,
  administratorComments: 900,
  administratorSignature: 120,
  leaderCommitments: 1200,
  finalNote: 2000,
};

const parseScoreValue = (value?: string) => {
  if (!value) {
    return -1;
  }
  const trimmed = String(value).trim();
  // Reject non-numeric labels like "Insufficient feedback..."
  if (!/^\d/.test(trimmed)) {
    return -1;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : -1;
};

const parseScorePercent = (value?: string) => {
  const parsed = parseScoreValue(value);
  if (parsed === -1) {
    return 0;
  }
  return Math.max(0, Math.min(100, (parsed / 10) * 100));
};

type IScoreMeterRole = "student" | "coach";
type IScoreMeterTone = "mid" | "low" | "high" | "coach";

const getScoreMeterTone = (
  role: IScoreMeterRole,
  value?: string
): IScoreMeterTone => {
  if (role === "coach") {
    return "coach";
  }
  const parsed = parseScoreValue(value);
  if (parsed === -1) {
    return "mid";
  }
  // Student avg: survey accent by default; red when below 5.
  if (parsed < 5) {
    return "low";
  }
  return "mid";
};

const formatScoreDisplay = (value?: string) => {
  if (!value) {
    return "—";
  }
  const parsed = parseScoreValue(value);
  if (parsed === -1) {
    return value;
  }
  return parsed.toFixed(1);
};

const formatSeasonLabel = (value?: string) => {
  if (!value) {
    return "";
  }
  const parts = String(value).trim().split(/\s+/);
  if (!parts.length) {
    return "";
  }
  return parts
    .map((part, index) =>
      index === 0
        ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        : part
    )
    .join(" ");
};

const formatSubtitleLabel = (value?: string) => {
  if (!value) {
    return "";
  }
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

// Classroom report terminology patch.
// Keep all word replacements in this helper so the patch is easy to adjust.
const normalizeClassroomTerminology = <T,>(value: T): T => {
  if (typeof value === "string") {
    return value
      .replace(/\bAthletic Administrator\b/g, "School Admin")
      .replace(/\bathletic administrator\b/g, "school admin")
      .replace(/\bAD's\b/g, "School Admin's")
      .replace(/\bAD-coach\b/g, "school admin-teacher")
      .replace(/\bcoach-AD\b/g, "teacher-school admin")
      .replace(/\bAD\b/g, "School Admin")
      .replace(/\bCoaching Feedback Report\b/g, "Teaching Feedback Report")
      .replace(/\bTeam Connectedness\b/g, "Classroom Connectedness")
      .replace(/\bteam connectedness\b/g, "classroom connectedness")
      .replace(/\bteam culture\b/g, "classroom culture")
      .replace(/\bTeam Culture\b/g, "Classroom Culture")
      .replace(/\bCoach Score\b/g, "Teacher Score")
      .replace(/\bcoach score\b/g, "teacher score")
      .replace(/\bCoach Self-Score\b/g, "Teacher Self-Score")
      .replace(/\bcoach self-score\b/g, "teacher self-score")
      .replace(/\bCoach Comment Summaries\b/g, "Teacher Comment Summaries")
      .replace(/\bcoach comment summaries\b/g, "teacher comment summaries")
      .replace(/\bCoach Comments\b/g, "Teacher Comments")
      .replace(/\bcoach comments\b/g, "teacher comments")
      .replace(/\bCoach Responses\b/g, "Teacher Responses")
      .replace(/\bcoach responses\b/g, "teacher responses")
      .replace(/\bCoach's\b/g, "Teacher's")
      .replace(/\bcoach's\b/g, "teacher's")
      .replace(/\bCoaches\b/g, "Teachers")
      .replace(/\bcoaches\b/g, "teachers")
      .replace(/\bCoach\b/g, "Teacher")
      .replace(/\bcoach\b/g, "teacher")
      .replace(/\bSports\b/g, "Subjects")
      .replace(/\bsports\b/g, "subjects")
      .replace(/\bSport\b/g, "Subject")
      .replace(/\bsport\b/g, "subject")
      .replace(/\bAthletic Program\b/g, "Classroom Program")
      .replace(/\bathletic program\b/g, "classroom program")
      .replace(/\bAthletic\b/g, "Classroom")
      .replace(/\bathletic\b/g, "classroom")
      .replace(/\bAthletes\b/g, "Students")
      .replace(/\bathletes\b/g, "students")
      .replace(/\bAthlete\b/g, "Student")
      .replace(/\bathlete\b/g, "student")
      .replace(/\bPlayers\b/g, "Students")
      .replace(/\bplayers\b/g, "students")
      .replace(/\bPlayer\b/g, "Student")
      .replace(/\bplayer\b/g, "student") as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeClassroomTerminology(item)) as T;
  }
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).reduce(
      (next, key) => ({
        ...next,
        [key]: normalizeClassroomTerminology(
          (value as Record<string, unknown>)[key]
        ),
      }),
      {}
    ) as T;
  }
  return value;
};

// Apply classroom terminology only to report content fields.
// Do not normalize identity/master data like schoolName, coachName, or sportName.
// To disable this patch later, replace this function body with: return report;
const normalizeClassroomReportContent = (
  report: ICoachReportPreviewData
): ICoachReportPreviewData => ({
  ...report,
  reportSubtitle: normalizeClassroomTerminology(report.reportSubtitle),
  footerBrand: normalizeClassroomTerminology(report.footerBrand),
  overallSummary: normalizeClassroomTerminology(report.overallSummary),
  kpis: normalizeClassroomTerminology(report.kpis),
  highlightsStrengths: normalizeClassroomTerminology(report.highlightsStrengths),
  teamConnectedness: normalizeClassroomTerminology(report.teamConnectedness),
  suggestions: normalizeClassroomTerminology(report.suggestions),
  suggestionsQuote: normalizeClassroomTerminology(report.suggestionsQuote),
  suggestionsQuoteAuthor: normalizeClassroomTerminology(
    report.suggestionsQuoteAuthor
  ),
  resources: normalizeClassroomTerminology(report.resources),
  wiseLeadership: normalizeClassroomTerminology(report.wiseLeadership),
  expectations: normalizeClassroomTerminology(report.expectations),
  relationalWarmth: normalizeClassroomTerminology(report.relationalWarmth),
  studentCommentSummaries: normalizeClassroomTerminology(
    report.studentCommentSummaries
  ),
  coachCommentSummaries: normalizeClassroomTerminology(
    report.coachCommentSummaries
  ),
  parentFeedback: normalizeClassroomTerminology(report.parentFeedback),
  finalNoteTitle: normalizeClassroomTerminology(report.finalNoteTitle),
  finalNote: normalizeClassroomTerminology(report.finalNote),
  leaderCommitmentsIntro: normalizeClassroomTerminology(
    report.leaderCommitmentsIntro
  ),
  leaderCommitments: normalizeClassroomTerminology(report.leaderCommitments),
});

// Remove ad terminology any time by replacing the function body with: return report;
// const normalizeClassroomReportContent = (
//   report: ICoachReportPreviewData
// ): ICoachReportPreviewData => report;

const formatBadgeLabel = (value?: string) =>
  String(value || "").replace(/(\D)0+(\d)\b/g, "$1$2");

const sanitizeSuggestions = (suggestions: IReportSuggestion[] = []) =>
  suggestions
    .map((suggestion, index) => ({
      ...suggestion,
      key:
        suggestion && suggestion.key ? suggestion.key : `suggestion${index + 1}`,
      number: String(index + 1),
      title:
        suggestion && suggestion.title
          ? String(suggestion.title)
          : `Suggestion ${index + 1}`,
      body: suggestion && suggestion.body ? String(suggestion.body) : "",
      maxLength:
        suggestion && suggestion.maxLength
          ? Number(suggestion.maxLength)
          : COACH_REPORT_LIMITS.suggestionBody,
    }))
    .filter(
      (suggestion) =>
        suggestion.body.trim() ||
        !/^Suggestion\s+\d+$/i.test(suggestion.title.trim())
    );

const isNumericScore = (value?: string | number) => {
  if (typeof value === "number") {
    return value !== -1 && Number.isFinite(value);
  }
  if (value === undefined || value === null) {
    return false;
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "—" || trimmed === "-") {
    return false;
  }
  return parseScoreValue(trimmed) !== -1;
};

/** Format score as "9.6/10". Non-numeric messages stay as-is. */
const formatScoreOutOfTen = (value?: string | number) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  if (typeof value === "number") {
    return value === -1 ? "— / 10" : `${roundToTen(value)} / 10`;
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "—" || trimmed === "-") {
    return "—";
  }
  if (!isNumericScore(trimmed)) {
    return trimmed;
  }
  const parsed = parseScoreValue(trimmed);
  const whole = Math.abs(parsed - Math.round(parsed)) < 0.05;
  const display = whole ? String(Math.round(parsed)) : parsed.toFixed(1);
  return `${display}/10`;
};

/**
 * Matches dynamic scores in prose, matching report reference formatting:
 * 7.2/10, 10/10, 7.1–7.2/10, 6.8-6.9/10, 92%, 95%+
 */
const DYNAMIC_SCORE_PATTERN = /(\d+(?:\.\d+)?(?:\s*[–—-]\s*\d+(?:\.\d+)?)?\/10|\d+(?:\.\d+)?%\+?)/g;
const DYNAMIC_SCORE_ONLY_PATTERN = /^(?:\d+(?:\.\d+)?(?:\s*[–—-]\s*\d+(?:\.\d+)?)?\/10|\d+(?:\.\d+)?%\+?)$/;

const escapeHtmlText = (value: string) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const boldDynamicScoresHtml = (value: string) =>
  escapeHtmlText(value).replace(
    DYNAMIC_SCORE_PATTERN,
    '<strong class="coach-report-preview__dynamic-score">$1</strong>'
  );

const renderTextWithBoldScores = (value: string) => {
  const text = String(value || "");
  if (!text) {
    return text;
  }
  const parts = text.split(DYNAMIC_SCORE_PATTERN);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, index) =>
    DYNAMIC_SCORE_ONLY_PATTERN.test(part) ? (
      <strong
        className="coach-report-preview__dynamic-score"
        key={`score-${index}`}
      >
        {part}
      </strong>
    ) : (
      part
    )
  );
};

type IQuadrantKey =
  | "neglectful"
  | "transactional"
  | "permissive"
  | "transformational";

const QUADRANTS: Record<
  IQuadrantKey,
  { key: IQuadrantKey; title: string; description: (name: string) => string }
> = {
  neglectful: {
    key: "neglectful",
    title: "Neglectful Leader",
    description: (name) =>
      `${name}'s scores place them in the bottom-left quadrant — lower relational warmth combined with lower expectations. This Neglectful Leader profile often leaves athletes feeling neither supported nor challenged.`,
  },
  transactional: {
    key: "transactional",
    title: "Transactional (Authoritative) Leader",
    description: (name) =>
      `${name}'s scores place them in the top-left quadrant — meaningful expectations with lower relational warmth. This Transactional (Authoritative) Leader profile emphasizes standards, but may leave athletes feeling less supported.`,
  },
  permissive: {
    key: "permissive",
    title: "Permissive (Friend) Leader",
    description: (name) =>
      `${name}'s scores place them in the bottom-right quadrant — high relational warmth with lower expectations. This Permissive (Friend) Leader profile builds connection, but may under-challenge athletes.`,
  },
  transformational: {
    key: "transformational",
    title: "Transformational (Empowering) Leader",
    description: (name) =>
      `${name}'s scores place them in the top-right quadrant — high relational warmth combined with meaningful expectations. This Transformational (Empowering) Leader profile is associated with athletes who feel both supported and challenged, and who tend to experience stronger motivation, trust, and growth over a season.`,
  },
};

const neutralizeNonResponse = (n: number) => (n === -1 ? 5 : n);

const getQuadrant = (warmth: number, expectations: number) => {
  const w = neutralizeNonResponse(warmth);
  const e = neutralizeNonResponse(expectations);
  if (w >= 5 && e >= 5) {
    return QUADRANTS.transformational;
  }
  if (w < 5 && e >= 5) {
    return QUADRANTS.transactional;
  }
  if (w >= 5 && e < 5) {
    return QUADRANTS.permissive;
  }
  return QUADRANTS.neglectful;
};

const isGenericCommentTitle = (title?: string) =>
  /^comment\s*\d+$/i.test((title || "").trim());

const parseCommentContent = (title: string, body: string) => {
  let text = (body || "").trim();
  // Number badge already shows index — drop leading "1." / "1)"
  text = text.replace(/^\d+[\.\)]\s*/, "");

  if (title && !isGenericCommentTitle(title)) {
    return {
      label: title.trim(),
      bodyText: text,
    };
  }

  const labeled = text.match(/^([^:]{1,100}):\s*([\s\S]*)$/);
  if (labeled) {
    return {
      label: labeled[1].trim(),
      bodyText: (labeled[2] || "").trim(),
    };
  }

  return {
    label: "",
    bodyText: text,
  };
};

interface IReportKpi {
  key: string;
  label: string;
  value: string;
}

interface IReportHighlight {
  key: string;
  badge: string;
  title: string;
  body: string;
  maxLength: number;
}

interface IReportSuggestion {
  key: string;
  number: string;
  title: string;
  body: string;
  maxLength: number;
}

interface IReportTableRow {
  key: string;
  label: string;
  score: string;
}

interface IReportComparisonRow {
  key: string;
  label: string;
  studentScore: string;
  coachScore: string;
}

interface IReportResource {
  key: string;
  category: string;
  title: string;
  body: string;
  whyChosen: string;
}

interface IReportCommentItem {
  key: string;
  title: string;
  body: string;
}

interface IReportCommentSection {
  key: string;
  question: string;
  comments: IReportCommentItem[];
}

interface ICoachCommentSection {
  key: string;
  question: string;
  placeholder: string;
}

interface ITeamConnectedness {
  intro?: string;
  overallScore: string;
  rows: IReportTableRow[];
  footerNote?: string;
}

interface IWiseLeadership {
  quadrant: string;
  summary: string;
  gridPlacement: string;
  administratorComments: string;
  administratorSignature: string;
}

interface IComparisonSection {
  title: string;
  averageLabel: string;
  studentAverage: string;
  coachAverage: string;
  summary: string;
  rows: IReportComparisonRow[];
}

interface IParentFeedback {
  rows: IReportTableRow[];
  average: string;
  summary: string;
  commentSections: IReportCommentSection[];
}

interface ICoachReportPreviewData {
  reportId?: number | null;
  schoolName: string;
  reportSubtitle: string;
  seasonLabel: string;
  coachName: string;
  sportName: string;
  schoolLogoUrl?: string;
  schoolLogoText: string;
  footerBrand: string;
  overallSummary: string;
  overallSummaryMaxLength: number;
  kpis: IReportKpi[];
  highlightsStrengths: IReportHighlight[];
  teamConnectedness: ITeamConnectedness;
  suggestions: IReportSuggestion[];
  suggestionsQuote: string;
  suggestionsQuoteAuthor: string;
  resources: IReportResource[];
  wiseLeadership: IWiseLeadership;
  expectations: IComparisonSection;
  relationalWarmth: IComparisonSection;
  studentCommentSummaries: IReportCommentSection[];
  coachCommentSummaries: ICoachCommentSection[];
  parentFeedback: IParentFeedback;
  finalNoteTitle: string;
  finalNote: string;
  finalNoteMaxLength: number;
  leaderCommitmentsIntro: string;
  leaderCommitments: string;
  leaderCommitmentsMaxLength: number;
}

interface ICoachReportPreviewProps {
  schoolId?: number;
  sportId?: number;
  coachId?: number;
  surveyId?: number;
  year?: number;
  type?: string;
  isParentResponses?: boolean;
  isPrintMode?: boolean;
  survey?: ISurveyEvalResponse;
  directorComments?: string;
  onDirectorCommentsChange?: (
    event: React.ChangeEvent<any>
  ) => void;
  onSaveAdComments?: () => void;
  coachName?: string;
  primaryColor?: string;
  isAdSurvey?: boolean;
  reportId?: number | null;
  readOnly?: boolean;
  onSaveReport?: (chatgptChangeResposne: Record<string, unknown>) => Promise<boolean>;
  displayMode?: "full" | "agenda" | "printData";
  initialReportData?: Partial<ICoachReportPreviewData> | null;
  pageName?: "reportmodal" | "evalprintform";
  /** Open Chrome/Google PDF print (same design as survey-data page). */
  autoPrint?: boolean;
  /** Pre-opened window from user click (avoids popup block → parent page print). */
  autoPrintWindow?: any;
  onAutoPrintHandled?: () => void;
  /** Bind sync print opener so parent can call it on user click (no blank about:blank wait). */
  bindOpenPrint?: (fn: ((targetWindow?: any) => void) | null) => void;
  schoolName?: string;
  sportName?: string;
  schoolLogoUrl?: string;
  schoolLogoText?: string;
  seasonLabel?: string;
}

const initialCoachReportData: ICoachReportPreviewData = {
  schoolName: "Grand Rapids Christian HS",
  reportSubtitle: "Teaching Feedback Report",
  seasonLabel: "Spring 2026",
  coachName: "Dan VanderPloeg",
  sportName: "Bowling",
  schoolLogoUrl: "",
  schoolLogoText: "GR",
  footerBrand: "Grand Rapids Christian HS - Teaching Feedback Report Spring 2026",
  overallSummary:
    "Coach Dan VanderPloeg earned strong respect from students and parents for fostering a supportive, character-driven classroom environment. Key strengths include student motivation, integrity, and a culture of belonging. Primary growth areas are lesson structure and individualized feedback.",
  overallSummaryMaxLength: COACH_REPORT_LIMITS.overallSummary,
  kpis: [
    { key: "expectationsScore", label: "Expectations / 10", value: "6.5" },
    { key: "relationalWarmthScore", label: "Relational Warmth / 10", value: "7.2" },
    { key: "leaderClassification", label: "Leader Classification", value: "Transformational" },
    { key: "teamConnectednessScore", label: "Classroom Connectedness / 10", value: "7.2" },
  ],
  highlightsStrengths: [
    {
      key: "highlightStrength01",
      badge: "Strength 01",
      title: "Consistent Positive Motivation",
      body:
        "At 8.0/10, positive motivation is the single highest-rated item in the entire student survey. Students experience teaching that redirects energy constructively, celebrates effort, and keeps setbacks in perspective.",
      maxLength: COACH_REPORT_LIMITS.highlightBody,
    },
    {
      key: "highlightStrength02",
      badge: "Strength 02",
      title: "Character & Integrity",
      body:
        "Students and parents both connect the classroom with Christian character, fairness, and emotional steadiness. This consistency between stated values and observed behavior builds trust with students and families alike.",
      maxLength: COACH_REPORT_LIMITS.highlightBody,
    },
    {
      key: "highlightStrength03",
      badge: "Strength 03",
      title: "Culture of Belonging",
      body:
        "Classroom connectedness scores suggest that students generally feel respected, safe, and included. Parent comments reinforce the same theme, pointing to a classroom environment where confidence and connection grow together.",
      maxLength: COACH_REPORT_LIMITS.highlightBody,
    },
  ],
  teamConnectedness: {
    overallScore: "7.2/10",
    rows: [
      { key: "teamConnectedness01", label: "Students feel safe to speak honestly", score: "7.3/10" },
      { key: "teamConnectedness02", label: "Students feel valued beyond performance", score: "7.2/10" },
      { key: "teamConnectedness03", label: "Students are proud to be part of the class", score: "7.5/10" },
      { key: "teamConnectedness04", label: "Students believe their input matters", score: "6.9/10" },
    ],
  },
  suggestions: [
    {
      key: "suggestion01",
      number: "1",
      title: "Keep: Enhance Individualized Feedback",
      body:
        "Build a repeatable rhythm for short student check-ins so each learner receives clear academic feedback and personal encouragement throughout the week.",
      maxLength: COACH_REPORT_LIMITS.suggestionBody,
    },
    {
      key: "suggestion02",
      number: "2",
      title: "Keep: Capitalize on Positive Motivation",
      body:
        "Turn the already-strong motivational culture into a visible classroom habit through peer recognition, class shout-outs, and consistent language around effort.",
      maxLength: COACH_REPORT_LIMITS.suggestionBody,
    },
    {
      key: "suggestion03",
      number: "3",
      title: "Start: Strengthen Practice Organization",
      body:
        "Introduce a clearer weekly lesson template so students know the structure, purpose, and expected outcome of each class session.",
      maxLength: COACH_REPORT_LIMITS.suggestionBody,
    },
    {
      key: "suggestion04",
      number: "4",
      title: "Start: Advance Use of Objective Feedback Tools",
      body:
        "Use simple review or progress-tracking tools to create more objective teaching feedback and show growth over time.",
      maxLength: COACH_REPORT_LIMITS.suggestionBody,
    },
    {
      key: "suggestion05",
      number: "5",
      title: "Start: Expand Classroom Connectedness Initiatives",
      body:
        "Create intentional feedback moments and culture-building structures so quieter students also feel heard and included.",
      maxLength: COACH_REPORT_LIMITS.suggestionBody,
    },
  ],
  suggestionsQuote: "Education is not the filling of a pail, but the lighting of a fire.",
  suggestionsQuoteAuthor: "William Butler Yeats",
  resources: [
    {
      key: "resource01",
      category: "Book",
      title: "Teach Like a Champion - Doug Lemov",
      body:
        "A practical, research-informed guide to building effective classroom routines. Lemov offers concrete techniques for lesson structure, questioning, feedback, student participation, and high expectations. The book is especially useful for teachers who want repeatable classroom moves that make learning time more purposeful, inclusive, and responsive.",
      whyChosen:
        "Survey responses indicate a desire for more structured classroom experiences and more individualized feedback. This book provides concrete templates and strategies that can directly address both areas, especially routines for checking understanding, strengthening participation, and giving clear academic feedback. Available in most libraries or online bookstores.",
    },
    {
      key: "resource02",
      category: "Article / Newsletter",
      title: "Cult of Pedagogy Newsletter - Jennifer Gonzalez",
      body:
        "Cult of Pedagogy offers accessible articles and newsletter resources on instructional practice, student engagement, classroom culture, assessment, and feedback. The resource is practical and classroom-centered, making it useful for translating survey themes into specific teaching adjustments.",
      whyChosen:
        "The feedback around individualized support and relational warmth aligns directly with this resource's focus. It can help turn a strong classroom foundation into consistent routines for student voice, feedback, and engagement. Available through the Cult of Pedagogy website or email newsletter.",
    },
    {
      key: "resource03",
      category: "Podcast",
      title: "The Learner Lab Podcast - Trevor Ragan",
      body:
        "The Learner Lab is a research-driven podcast focused on growth mindset, resilience, motivation, and learning science. Host Trevor Ragan translates complex research into practical strategies for helping students frame mistakes productively, build intrinsic motivation, and take ownership of learning.",
      whyChosen:
        "This podcast is relevant when feedback points to psychological safety, student ownership, and productive response to mistakes. Episodes on growth mindset, feedback, and learning culture offer low-barrier strategies that can be implemented immediately. Available on Spotify and Apple Podcasts.",
    },
    {
      key: "resource04",
      category: "Book",
      title: "The Culture Code - Daniel Coyle",
      body:
        "Daniel Coyle examines the hidden dynamics that make great groups work, drawing on research inside elite organizations - from the Navy SEALs to Pixar. He identifies three core skills that build cohesive, high-performing cultures: building safety, sharing vulnerability, and establishing purpose. The book is packed with concrete, immediately actionable practices for leaders at every level.",
      whyChosen:
        `When student feedback points to whether every student feels heard and whether difficult issues can be raised, there is a clear opportunity to deepen classroom psychological safety. Coyle's frameworks for building belonging and trust map directly onto these areas and provide a practical roadmap for the school year.`,
    },
  ],
  wiseLeadership: {
    quadrant: "Transformational (Empowering) Leader",
    summary:
      "Coach Dan's scores place him in the top-right quadrant - high relational warmth combined with meaningful expectations. This is the ideal coaching zone, associated with the highest levels of athlete motivation, trust, and long-term development. Coaches in this quadrant are described by athletes as both demanding and deeply supportive - they push players to grow while making them feel safe enough to take risks and fail forward.",
    gridPlacement:
      "Upper-right quadrant: high relational warmth paired with healthy standards and athlete empowerment.",
    administratorComments:
      "Another good season, coach. Grateful for the steady investment in both the program and the athletes as we continue building long-term momentum.",
    administratorSignature: "- JH, Athletic Administrator",
  },
  expectations: {
    title: "Expectations",
    averageLabel: "Expectations Average",
    studentAverage: "6.5",
    coachAverage: "7.3",
    summary:
      "Averaged 6.5/10 student and 7.3/10 coach. Strongest areas include on-field performance expectations and Christian integrity. Biggest gaps remain film use and off-season programming.",
    rows: [
      { key: "expectations01", label: "My coach is innovative and studies the sport", studentScore: "8.3", coachScore: "8.5" },
      { key: "expectations02", label: "My coach handles discipline fairly and appropriately", studentScore: "10.0", coachScore: "8.0" },
      { key: "expectations03", label: "My coach runs well-organized practices", studentScore: "6.5", coachScore: "8.0" },
      { key: "expectations04", label: "My coach uses film or objective tools to help me improve", studentScore: "3.4", coachScore: "5.0" },
      { key: "expectations05", label: "My coach develops athletes in the off-season", studentScore: "4.3", coachScore: "6.5" },
    ],
  },
  relationalWarmth: {
    title: "Relational Warmth",
    averageLabel: "Relational Warmth Average",
    studentAverage: "7.2",
    coachAverage: "8.1",
    summary:
      "Averaged 7.2/10 student and 8.1/10 coach, the highest-scoring student category. Positive motivation stands out most, while psychological safety still has room to deepen.",
    rows: [
      { key: "relational01", label: "Focuses on learning over winning - no fear of mistakes", studentScore: "7.3", coachScore: "8.0" },
      { key: "relational02", label: "Values each player's unique skills and talents", studentScore: "7.3", coachScore: "8.5" },
      { key: "relational03", label: "Corrections feel encouraging, not discouraging", studentScore: "7.3", coachScore: "8.0" },
      { key: "relational04", label: "Positively motivates - no threats or punishments", studentScore: "8.0", coachScore: "9.0" },
      { key: "relational05", label: "Every member feels heard and appreciated", studentScore: "6.9", coachScore: "7.5" },
    ],
  },
  studentCommentSummaries: [
    {
      key: "studentSummary01",
      question: "What I appreciate most about coach is...",
      comments: [
        { key: "studentSummary01Comment01", title: "Engaging Style", body: "Invests time in skill development, maintains positivity regardless of outcomes." },
        { key: "studentSummary01Comment02", title: "Supportive Mentoring", body: "Fosters player success, shares knowledge, and encourages a positive mindset." },
        { key: "studentSummary01Comment03", title: "Balanced Approach", body: "Focuses on improvement without overwhelming players with pressure." },
      ],
    },
    {
      key: "studentSummary02",
      question: "I would like coach to improve on...",
      comments: [
        { key: "studentSummary02Comment01", title: "Structured Practices", body: "More organization and coach-led activities for focused skill development." },
        { key: "studentSummary02Comment02", title: "Individual Attention", body: "More personalized feedback, especially during warm-ups and individual sessions." },
        { key: "studentSummary02Comment03", title: "Match Observation", body: "Closer observation during matches with more targeted skill feedback." },
      ],
    },
    {
      key: "studentSummary03",
      question: "The most important thing I learned this year was...",
      comments: [
        { key: "studentSummary03Comment01", title: "Team Support", body: "A team's constant backing matters more than individual struggles." },
        { key: "studentSummary03Comment02", title: "Mental Resilience", body: "Calmness and positive attitude under high-pressure situations." },
        { key: "studentSummary03Comment03", title: "Personal Growth", body: "Self-improvement over comparison - try harder for yourself, not others." },
      ],
    },
    {
      key: "studentSummary04",
      question: "Positive aspects of spiritual development this year...",
      comments: [
        { key: "studentSummary04Comment01", title: "Team Bonding", body: "Pre-match prayers and strong friendships fostered community." },
        { key: "studentSummary04Comment02", title: "Positive Influence", body: "Coaches led by example in sportsmanship with other teams." },
        { key: "studentSummary04Comment03", title: "Supportive Environment", body: "Emotional resilience and inner peace were promoted through team unity." },
      ],
    },
  ],
  coachCommentSummaries: [
    { key: "coachSummary01", question: "What I feel went well this season...", placeholder: "Coach comments will appear here when submitted through the platform." },
    { key: "coachSummary02", question: "Areas I want to grow in next season...", placeholder: "Coach comments will appear here when submitted through the platform." },
    { key: "coachSummary03", question: "What I learned about my team this year...", placeholder: "Coach comments will appear here when submitted through the platform." },
    { key: "coachSummary04", question: "My goals for the program going forward...", placeholder: "Coach comments will appear here when submitted through the platform." },
  ],
  parentFeedback: {
    rows: [
      { key: "parentFeedback01", label: "I was fully informed each week on what was happening with the team.", score: "9.6/10" },
      { key: "parentFeedback02", label: "Coach communicates schedule changes clearly and in a timely manner.", score: "10/10" },
      { key: "parentFeedback03", label: "Christian Character Development was a prominent part of my child's experience.", score: "9.2/10" },
      { key: "parentFeedback04", label: "Coach starts and ends practice on time.", score: "10/10" },
    ],
    average: "9.7/10",
    summary:
      "Parent satisfaction for this program is exceptionally strong. Families especially value communication reliability, consistency, and the visible presence of character development within the team experience. These results point to a high degree of trust in the overall direction of the program.",
    commentSections: [
      {
        key: "parentSummary01",
        question: "Coach is doing a great job with...",
        comments: [
          { key: "parentSummary01Comment01", title: "Player Development", body: "Fosters growth, improving both skills and confidence within the team." },
          { key: "parentSummary01Comment02", title: "Successful Leadership", body: "Patient, kind, and encouraging leadership that builds belief." },
        ],
      },
      {
        key: "parentSummary02",
        question: "What did coach seem to value most?",
        comments: [
          { key: "parentSummary02Comment01", title: "People Over Scores", body: "Prioritized individual development and well-being over results." },
          { key: "parentSummary02Comment02", title: "Skill Development", body: "Balanced athletic growth with school and personal demands." },
        ],
      },
      {
        key: "parentSummary03",
        question: "How has this season prepared your child for life after high school?",
        comments: [
          { key: "parentSummary03Comment01", title: "Life-Long Lessons", body: "Built teamwork skills and self-belief that extend beyond the sport." },
          { key: "parentSummary03Comment02", title: "Resilience", body: "Learned emotional management through disappointment and challenge." },
        ],
      },
      {
        key: "parentSummary04",
        question: "Areas for coach to improve...",
        comments: [
          { key: "parentSummary04Comment01", title: "Technical Coaching", body: "Opportunity to strengthen teaching around technique, equipment, and lane conditions." },
          { key: "parentSummary04Comment02", title: "Coaching Depth", body: "Strong facilitator, but some responses note a gap in technical detail." },
        ],
      },
      {
        key: "parentSummary05",
        question: "Suggestions for improving the 7-12 athletic program...",
        comments: [
          { key: "parentSummary05Comment01", title: "Bowling Events", body: "Host events and fundraisers that grow interest and engagement around the program." },
          { key: "parentSummary05Comment02", title: "Middle School Coach", body: "Consider dedicated middle school support to reduce the overall coaching burden." },
        ],
      },
    ],
  },
  finalNoteTitle: "A Message to Dan VanderPloeg",
  finalNote:
    "Dan, thank you for another meaningful and impactful season. The data from this evaluation tells a clear and encouraging story: you are a coach who leads with character, builds genuine relationships, and creates an environment where young people feel safe to grow — both as athletes and as individuals. A 97% parent satisfaction score and a 7.2/10 team connectedness rating are not just numbers; they are a reflection of the trust you have built with every family and player in this program.\n\nYour highest-rated quality — positive motivation (8.0/10) — is one of the most powerful tools a coach can possess. Research consistently shows that athletes who feel genuinely encouraged, rather than pressured, develop faster, stay in sport longer, and carry the lessons they learn into adulthood. The students on your team are experiencing that firsthand. The parent comments reinforce this: words like \"patient,\" \"kind,\" \"encouraging,\" and \"led us to state championships\" speak to a coach who has earned the respect of the entire program community.\n\nThe growth areas identified in this report — practice structure, individualized feedback, and technical development tools — are not weaknesses of character; they are structural opportunities. You already have the relational foundation and the motivational culture that many coaches spend years trying to build. The next chapter of your development is about channeling that foundation into even more intentional, organized, and technically rich coaching experiences for your athletes.\n\nThe resources and suggestions in this report are offered in that spirit — not as corrections, but as tools to help you go from excellent to exceptional. We are grateful for your commitment to Grand Rapids Christian HS, to the sport of bowling, and above all, to the young men and women in your care. Keep going, Coach.",
  finalNoteMaxLength: COACH_REPORT_LIMITS.finalNote,
  leaderCommitmentsIntro:
    "Based on this evaluation, Coach Dan has identified the following personal commitments and goals for the upcoming season. These will be reviewed at the start of next year's evaluation cycle.",
  leaderCommitments:
    "Create a clearer weekly practice plan with defined skill blocks and communication goals.\nIncrease individual player feedback through short post-practice check-ins.\nIntroduce at least one simple objective feedback tool for technical development.",
  leaderCommitmentsMaxLength: COACH_REPORT_LIMITS.leaderCommitments,
};

const CoachReportPreview = ({
  schoolId,
  sportId,
  coachId,
  surveyId,
  year,
  type = "coach",
  isParentResponses = false,
  isPrintMode = false,
  survey,
  coachName,
  primaryColor,
  isAdSurvey = false,
  reportId: reportIdProp,
  readOnly = false,
  onSaveReport,
  displayMode = "full",
  initialReportData = null,
  pageName,
  autoPrint = false,
  autoPrintWindow = null,
  onAutoPrintHandled,
  bindOpenPrint,
}: ICoachReportPreviewProps) => {
  const [report, setReport] = React.useState<ICoachReportPreviewData>(
    // Normalize initial display content before the first render.
    normalizeClassroomReportContent({
      ...initialCoachReportData,
      ...(initialReportData || {}),
      reportId: reportIdProp || null,
    })
  );
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(
    Boolean(
      !initialReportData && schoolId && sportId && coachId && !readOnly
    )
  );
  const [hasPreviewData, setHasPreviewData] = React.useState<boolean | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  // const [lastRemovedResource, setLastRemovedResource] = React.useState<{
  //   resource: IReportResource;
  //   index: number;
  // } | null>(null);
  const reportRef = React.useRef(report);
  const loadedParamsRef = React.useRef<string | null>(null);
  const didAutoPrintRef = React.useRef(false);
  // Report history opens this preview in read-only mode, so all editable
  // textareas must behave like print/view-only fields there.
  const isEditable = !isPrintMode && !readOnly;
  const terminology = React.useMemo(
    () =>
      isAdSurvey
        ? {
            reportSubject: "AD",
            respondent: "Coach",
            respondentShort: "Coach",
            respondentLower: "coach",
            selfScore: "AD",
            selfScoreLabel: "AD Score",
            commentSummaries: "AD Comment Summaries",
            responsePlaceholder:
              "AD comments will appear here when submitted through the platform.",
            commitments: "AD Commitments",
            commitmentsPlaceholder:
              "AD's personal commitments and goals for the upcoming season will be recorded here during the AD-coach meeting.",
          }
        : {
          reportSubject: "Teacher",
            respondent: "Student",
            respondentShort: "Stud",
            respondentLower: "student",
            selfScore: "Teacher",
            selfScoreLabel: "Teacher Self-Score",
            commentSummaries: "Teacher Comment Summaries",
            responsePlaceholder:
              "Teacher comments will appear here when submitted through the platform.",
            commitments: "Teacher Commitments",
            commitmentsPlaceholder:
              "Teacher's personal commitments and goals for the upcoming season will be recorded here during the school admin-teacher meeting.",
          },
    [isAdSurvey]
  );

  React.useEffect(() => {
    reportRef.current = report;
  }, [report]);

  React.useEffect(() => {
    if (hasUnsavedChanges || isLoadingPreview) {
      return;
    }
    // Clean loaded report content once data settles; skip while user is editing.
    const normalizedReport = normalizeClassroomReportContent(report);
    if (JSON.stringify(normalizedReport) !== JSON.stringify(report)) {
      setReport(normalizedReport);
    }
  }, [hasUnsavedChanges, isLoadingPreview, report]);

  React.useEffect(() => {
    if (initialReportData) {
      loadedParamsRef.current = "initial";
      setHasPreviewData(true);
      setReport((current) =>
        // Normalize content received from parent/report history.
        normalizeClassroomReportContent({
          ...current,
          ...initialReportData,
          suggestions: sanitizeSuggestions(initialReportData.suggestions || current.suggestions),
          resources: sanitizeResources(initialReportData.resources || current.resources),
          reportId:
            (initialReportData && initialReportData.reportId) ||
            reportIdProp ||
            current.reportId ||
            null,
        })
      );
      setHasUnsavedChanges(false);
      // setLastRemovedResource(null);
      setIsLoadingPreview(false);
      return;
    }

    if (reportIdProp) {
      setReport((current) =>
        current.reportId === reportIdProp
          ? current
          : { ...current, reportId: reportIdProp }
      );
    }
  }, [initialReportData, reportIdProp]);

  const loadReportPreview = React.useCallback(
    async (options?: { force?: boolean }) => {
      if (initialReportData) {
        return;
      }
      if (!schoolId || !sportId || !coachId) {
        return;
      }

      const resolvedYear = year || new Date().getFullYear();
      const paramsKey = [
        schoolId,
        sportId,
        coachId,
        surveyId,
        resolvedYear,
        type,
        isParentResponses,
      ].join("|");

      if (!options?.force && loadedParamsRef.current === paramsKey) {
        return;
      }

      try {
        // Hidden PRINT DATA ONLY preload (readOnly) must stay mounted — never block print.
        if (!readOnly) {
          setIsLoadingPreview(true);
        }
       
        const response = await axios.post(
          apiUrl("/reports/survey/evaluation/report-preview/by-ids"),
          {
            schoolId,
            sportId,
            coachId,
            surveyId,
            year: resolvedYear,
            type,
            isParentResponses,
          },
          { withCredentials: true }
        );
         console.log("preview response", response)
        const result = response.data;
        if (result?.status && result?.data) {
          loadedParamsRef.current = paramsKey;
          setHasPreviewData(true);
          // Normalize content received from the preview API.
          setReport(
            normalizeClassroomReportContent({
              ...result.data,
              suggestions: sanitizeSuggestions(result.data.suggestions || []),
              resources: sanitizeResources(result.data.resources || []),
              finalNoteTitle: `A Message to ${
                (result.data.coachName && String(result.data.coachName).trim()) ||
                "Teacher"
              }`,
              reportId: result.data.reportId || reportIdProp || null,
            })
          );
          setSaveStatus(
            result.data.reportId || reportIdProp
              ? ""
              : "Report id missing — save unavailable."
          );
          setHasUnsavedChanges(false);
          // setLastRemovedResource(null);
          return;
        }

        setHasPreviewData(false);
        setSaveStatus("No report data available for this sport.");
        // Keep fallback content aligned with classroom terminology too.
        setReport(
          normalizeClassroomReportContent({
            ...initialCoachReportData,
            reportId: reportIdProp || null,
          })
        );
      } catch (error) {
        console.error("Failed to load coach report preview", error);
        setHasPreviewData(false);
        setSaveStatus("No report data available for this sport.");
        // Keep fallback content aligned with classroom terminology too.
        setReport(
          normalizeClassroomReportContent({
            ...initialCoachReportData,
            reportId: reportIdProp || null,
          })
        );
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [
      schoolId,
      sportId,
      coachId,
      surveyId,
      year,
      type,
      isParentResponses,
      reportIdProp,
      readOnly,
      initialReportData,
    ]
  );

  React.useEffect(() => {
    void loadReportPreview();
  }, [loadReportPreview]);

  const getResourceVariantClass = (category: string) => {
    const normalizedCategory = category.toLowerCase();

    if (normalizedCategory.indexOf("article") >= 0) {
      return "coach-report-preview__resource-card-big--article";
    }

    if (normalizedCategory.indexOf("podcast") >= 0) {
      return "coach-report-preview__resource-card-big--podcast";
    }

    return "coach-report-preview__resource-card-big--book";
  };

  const renderSectionHeader = (title: string) => (
    <div className="coach-report-preview__section-header">
      <div className="coach-report-preview__section-bar" />
      <div className="coach-report-preview__section-title">{title}</div>
    </div>
  );

  const updateReport = (updater: (current: ICoachReportPreviewData) => ICoachReportPreviewData) => {
    setReport((current) => updater(current));
    setHasUnsavedChanges(true);
    setSaveStatus("");
  };

  const updateResource = (
    resourceKey: string,
    updater: (resource: IReportResource) => IReportResource
  ) => {
    updateReport((current) => ({
      ...current,
      resources: current.resources.map((resource) =>
        resource.key === resourceKey ? updater(resource) : resource
      ),
    }));
  };

  const sanitizeResources = (resources: IReportResource[] = []) =>
    resources
      .map((resource, index) => ({
        ...resource,
        key: resource && resource.key ? resource.key : `resource${index + 1}`,
        category:
          resource && resource.category ? String(resource.category) : "Book",
        title: resource && resource.title ? String(resource.title) : "",
        body: resource && resource.body ? String(resource.body) : "",
        whyChosen:
          resource && resource.whyChosen ? String(resource.whyChosen) : "",
      }))
      .filter(
        (resource) =>
          resource.title.trim() ||
          resource.body.trim() ||
          resource.whyChosen.trim()
      );

  const removeResource = (
    itemKey: string,
    section: "resources" | "suggestions" = "resources"
  ) => {
    setReport((current) => {
      const currentItems =
        section === "suggestions" ? current.suggestions : current.resources;
      if (currentItems.length <= 1) {
        const browserWindow: any = Function("return this")();
        if (browserWindow && browserWindow.alert) {
          browserWindow.alert(
            `You can't delete all ${section}. At least one ${section.slice(
              0,
              -1
            )} should be available.`
          );
        }
        return current;
      }

      const removedIndex = currentItems.findIndex((item) => item.key === itemKey);
      if (removedIndex < 0) {
        return current;
      }

      // setLastRemovedResource({
      //   resource: current.resources[removedIndex],
      //   index: removedIndex,
      // });
      setHasUnsavedChanges(true);
      setSaveStatus("");

      return section === "suggestions"
        ? {
            ...current,
            suggestions: current.suggestions
              .filter((suggestion) => suggestion.key !== itemKey)
              .map((suggestion, index) => ({
                ...suggestion,
                number: String(index + 1),
              })),
          }
        : {
            ...current,
            resources: current.resources.filter(
              (resource) => resource.key !== itemKey
            ),
          };
    });
  };

  // const undoRemoveResource = () => {
  //   if (!lastRemovedResource) {
  //     return;
  //   }

  //   updateReport((current) => {
  //     const nextResources = current.resources.slice();
  //     const restoreIndex = Math.min(
  //       Math.max(lastRemovedResource.index, 0),
  //       nextResources.length
  //     );
  //     nextResources.splice(restoreIndex, 0, lastRemovedResource.resource);

  //     return {
  //       ...current,
  //       resources: nextResources,
  //     };
  //   });
  //   setLastRemovedResource(null);
  //   setSaveStatus("");
  //   const browserWindow: any = Function("return this")();
  //   if (browserWindow && browserWindow.setTimeout) {
  //     browserWindow.setTimeout(scheduleReportTextareaLayout, 0);
  //   }
  // };

  const buildEditablePayload = (current: ICoachReportPreviewData) => ({
    overallSummary: current.overallSummary,
    overallSummaryMaxLength: current.overallSummaryMaxLength,
    kpis: current.kpis,
    highlightsStrengths: current.highlightsStrengths,
    teamConnectedness: current.teamConnectedness,
    suggestions: current.suggestions,
    suggestionsQuote: current.suggestionsQuote,
    suggestionsQuoteAuthor: current.suggestionsQuoteAuthor,
    resources: sanitizeResources(current.resources),
    wiseLeadership: {
      quadrant: current.wiseLeadership.quadrant,
      summary: current.wiseLeadership.summary,
      gridPlacement: current.wiseLeadership.gridPlacement,
      administratorComments: current.wiseLeadership.administratorComments,
      administratorSignature: current.wiseLeadership.administratorSignature,
    },
    expectations: {
      ...current.expectations,
    },
    relationalWarmth: {
      ...current.relationalWarmth,
    },
    parentFeedback: {
      summary: current.parentFeedback.summary,
    },
    finalNoteTitle: current.finalNoteTitle,
    finalNote: current.finalNote,
    finalNoteMaxLength: current.finalNoteMaxLength,
    leaderCommitmentsIntro: current.leaderCommitmentsIntro,
    leaderCommitments: current.leaderCommitments,
    leaderCommitmentsMaxLength: current.leaderCommitmentsMaxLength,
  });

  const saveReport = async () => {
    if (isPrintMode || !onSaveReport) {
      return;
    }

    const currentReport = reportRef.current;
    const reportId = currentReport.reportId || reportIdProp || null;

    if (!reportId) {
      setSaveStatus("Cannot save — report id is missing.");
      return;
    }

    try {
      setIsSaving(true);
      setSaveStatus("Saving...");
      const didSave = await onSaveReport(buildEditablePayload(currentReport));

      if (!didSave) {
        setSaveStatus("Save failed.");
        return;
      }

      // Reload the latest saved report so the preview always reflects current DB data.
      await loadReportPreview({ force: true });
      setSaveStatus("Changes saved.");
      setHasUnsavedChanges(false);
      // setLastRemovedResource(null);
    } catch (error) {
      console.error("Failed to save coach report preview", error);
      setSaveStatus("Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const printRootRef = React.useRef<any>(null);
  const layoutFramePendingRef = React.useRef(false);
  const layoutFrameRequestedAgainRef = React.useRef(false);

  const GROWABLE_TEXTAREA_SELECTOR = [
    "textarea.coach-report-preview__editable-text--suggestion",
    "textarea.coach-report-preview__editable-text--summary",
    "textarea.coach-report-preview__editable-text--parent-summary",
    "textarea.coach-report-preview__editable-text--final-note",
    "textarea.coach-report-preview__editable-text--commitments",
    "textarea.coach-report-preview__editable-text--resource-title",
    "textarea.coach-report-preview__editable-text--resource-body",
    "textarea.coach-report-preview__editable-text--resource-why-chosen",
    "textarea.coach-report-preview__admin-comments-body",
    "textarea.coach-report-preview__admin-comments-signature",
  ].join(",");

  const resizeHighlightTextarea = (el: any) => {
    if (!el || !el.style) {
      return;
    }
    // Grow to full content so the last line is never clipped.
    el.style.setProperty("max-height", "none", "important");
    el.style.setProperty("overflow", "hidden", "important");
    el.style.setProperty("overflow-x", "hidden", "important");
    el.style.setProperty("overflow-y", "hidden", "important");
    // Measure full value (including Enter / blank lines) so text does not jump.
    el.style.setProperty("height", "0px");
    el.scrollTop = 0;
    let contentHeight = el.scrollHeight || 0;
    const surface: any = el.parentNode;
    const printText: any =
      surface && surface.querySelector
        ? surface.querySelector(".coach-report-preview__print-text")
        : null;
    if (printText) {
      const prevBottom = printText.style.bottom;
      const prevHeight = printText.style.height;
      const prevOverflow = printText.style.overflow;
      const prevMaxHeight = printText.style.maxHeight;
      printText.style.bottom = "auto";
      printText.style.height = "auto";
      printText.style.maxHeight = "none";
      printText.style.overflow = "visible";
      const printHeight = printText.scrollHeight || 0;
      if (printHeight > contentHeight) {
        contentHeight = printHeight;
      }
      printText.style.bottom = prevBottom;
      printText.style.height = prevHeight;
      printText.style.maxHeight = prevMaxHeight;
      printText.style.overflow = prevOverflow;
    }
    // +2px buffer keeps descenders of the last line fully visible.
    el.style.setProperty("height", contentHeight + 2 + "px", "important");
    el.scrollTop = 0;
  };

  const syncHighlightHeights = () => {
    const root = printRootRef.current;
    if (!root || !root.querySelectorAll) {
      return;
    }

    // Clear any prior title min-heights so spacing matches reportt.html
    // (natural wrap; fixed 4px after title — no reserved 2-line band).
    const titles = root.querySelectorAll(
      ".coach-report-preview__highlight-title, .coach-report-preview__highlight-title-input"
    );
    Array.prototype.forEach.call(titles, (el: any) => {
      if (!el || !el.style) {
        return;
      }
      el.style.minHeight = "";
      el.style.height = "";
    });

    // Strength cards only: size each body to full text, then equalize to tallest.
    const areas = root.querySelectorAll(
      ".coach-report-preview__highlight-card textarea.coach-report-preview__editable-text--highlight"
    );
    if (!areas || !areas.length) {
      return;
    }
    Array.prototype.forEach.call(areas, (el: any) => {
      resizeHighlightTextarea(el);
    });
    let maxBodyHeight = 0;
    Array.prototype.forEach.call(areas, (el: any) => {
      const nextHeight = (el && (el.offsetHeight || el.scrollHeight)) || 0;
      if (nextHeight > maxBodyHeight) {
        maxBodyHeight = nextHeight;
      }
    });
    if (maxBodyHeight > 0) {
      Array.prototype.forEach.call(areas, (el: any) => {
        if (!el || !el.style) {
          return;
        }
        el.style.setProperty("height", maxBodyHeight + "px", "important");
      });
    }
  };

  const autoGrowTextareas = () => {
    const root = printRootRef.current;
    if (!root || !root.querySelectorAll) {
      return;
    }
    const areas = root.querySelectorAll(GROWABLE_TEXTAREA_SELECTOR);
    Array.prototype.forEach.call(areas, (el: any) => {
      if (!el || !el.style) {
        return;
      }
      el.style.overflowY = "hidden";
      el.style.height = "auto";
      el.scrollTop = 0;
      let nextHeight = (el && el.scrollHeight) || 0;
      const surface: any = el.parentNode;
      const printText: any =
        surface && surface.querySelector
          ? surface.querySelector(".coach-report-preview__print-text")
          : null;
      if (printText) {
        const prevBottom = printText.style.bottom;
        const prevHeight = printText.style.height;
        const prevOverflow = printText.style.overflow;
        printText.style.bottom = "auto";
        printText.style.height = "auto";
        printText.style.overflow = "visible";
        const printHeight = printText.scrollHeight || 0;
        if (printHeight > nextHeight) {
          nextHeight = printHeight;
        }
        printText.style.bottom = prevBottom;
        printText.style.height = prevHeight;
        printText.style.overflow = prevOverflow;
      }
      el.style.height = nextHeight + 2 + "px";
      el.scrollTop = 0;
    });
  };

  const applyRowHeight = (row: any, heightPx: number) => {
    if (!row || !row.style) {
      return;
    }
    row.style.setProperty("height", heightPx + "px", "important");
    row.style.setProperty("min-height", heightPx + "px", "important");
    const cells = row.cells || row.querySelectorAll("th, td");
    if (!cells) {
      return;
    }
    Array.prototype.forEach.call(cells, (cell: any) => {
      if (!cell || !cell.style) {
        return;
      }
      cell.style.setProperty("height", heightPx + "px", "important");
      cell.style.setProperty("min-height", heightPx + "px", "important");
      cell.style.setProperty("vertical-align", "middle", "important");
    });
  };

  const clearRowHeight = (row: any) => {
    if (!row || !row.style) {
      return;
    }
    row.style.removeProperty("height");
    row.style.removeProperty("min-height");
    const cells = row.cells || row.querySelectorAll("th, td");
    if (!cells) {
      return;
    }
    Array.prototype.forEach.call(cells, (cell: any) => {
      if (!cell || !cell.style) {
        return;
      }
      cell.style.removeProperty("height");
      cell.style.removeProperty("min-height");
    });
  };

  /** Build [0..count) without sparse Array(n) holes (forEach/map skip holes). */
  const rangeIndexes = (count: number) => {
    const indexes: number[] = [];
    let index = 0;
    while (index < count) {
      indexes.push(index);
      index += 1;
    }
    return indexes;
  };

  /** Pad shorter tbody so both comparison tables share the same row count. */
  const padComparisonTbody = (table: any, targetCount: number, doc: any) => {
    if (!table || !table.tBodies || !table.tBodies[0] || !doc) {
      return;
    }
    const tbody: any = table.tBodies[0];
    const colCount =
      table.tHead && table.tHead.rows && table.tHead.rows[0]
        ? table.tHead.rows[0].cells.length
        : 3;
    while (tbody.rows.length < targetCount) {
      const tr = doc.createElement("tr");
      tr.setAttribute("data-comparison-spacer", "1");
      Array.prototype.forEach.call(rangeIndexes(colCount), (c: number) => {
        const td = doc.createElement("td");
        td.innerHTML = "\u00a0";
        if (c > 0) {
          td.className = "coach-report-preview__score-col";
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    }
  };

  const syncComparisonTablesInRoot = (root: any) => {
    if (!root || !root.querySelectorAll) {
      return;
    }
    const grids = root.querySelectorAll(
      ".coach-report-preview__two-column-grid--comparison"
    );
    Array.prototype.forEach.call(grids, (grid: any) => {
      if (!grid || !grid.querySelectorAll) {
        return;
      }
      const tables = grid.querySelectorAll(
        ".coach-report-preview__comparison-table"
      );
      if (!tables || tables.length < 2) {
        return;
      }
      const leftTable: any = tables[0];
      const rightTable: any = tables[1];
      const ownerDoc = leftTable.ownerDocument || rightTable.ownerDocument;

      // Keep section titles the same height so tables start on one baseline.
      const leftHeader: any = grid.querySelectorAll(
        ".coach-report-preview__section-header"
      )[0];
      const rightHeader: any = grid.querySelectorAll(
        ".coach-report-preview__section-header"
      )[1];
      if (leftHeader && rightHeader && leftHeader.style && rightHeader.style) {
        leftHeader.style.height = "";
        rightHeader.style.height = "";
        const headerH = Math.max(
          leftHeader.offsetHeight || 0,
          rightHeader.offsetHeight || 0
        );
        if (headerH > 0) {
          leftHeader.style.setProperty("height", headerH + "px", "important");
          rightHeader.style.setProperty("height", headerH + "px", "important");
        }
      }

      // Equalize body row counts so footers line up top-to-bottom.
      const leftBodyCount =
        leftTable.tBodies && leftTable.tBodies[0]
          ? leftTable.tBodies[0].rows.length
          : 0;
      const rightBodyCount =
        rightTable.tBodies && rightTable.tBodies[0]
          ? rightTable.tBodies[0].rows.length
          : 0;
      const targetBodyCount = Math.max(leftBodyCount, rightBodyCount);
      if (ownerDoc) {
        padComparisonTbody(leftTable, targetBodyCount, ownerDoc);
        padComparisonTbody(rightTable, targetBodyCount, ownerDoc);
      }

      const syncRows = (selector: string) => {
        const leftRows = leftTable.querySelectorAll(selector);
        const rightRows = rightTable.querySelectorAll(selector);
        const rowCount = Math.max(leftRows.length, rightRows.length);
        const indexes = rangeIndexes(rowCount);
        Array.prototype.forEach.call(indexes, (index: number) => {
          clearRowHeight(leftRows[index]);
          clearRowHeight(rightRows[index]);
        });
        Array.prototype.forEach.call(indexes, (index: number) => {
          const leftRow: any = leftRows[index];
          const rightRow: any = rightRows[index];
          if (!leftRow || !rightRow) {
            return;
          }
          const maxHeight = Math.max(
            leftRow.offsetHeight || 0,
            rightRow.offsetHeight || 0
          );
          if (maxHeight > 0) {
            applyRowHeight(leftRow, maxHeight);
            applyRowHeight(rightRow, maxHeight);
          }
        });
      };
      syncRows("thead tr");
      syncRows("tbody tr");
      syncRows("tfoot tr");
    });
  };

  const syncComparisonRowHeights = () => {
    syncComparisonTablesInRoot(printRootRef.current);
  };

  const scheduleReportTextareaLayout = () => {
    const browserWindow: any = Function("return this")();
    if (layoutFramePendingRef.current) {
      layoutFrameRequestedAgainRef.current = true;
      return;
    }
    layoutFramePendingRef.current = true;

    const queueRun = (callback: () => void) => {
      if (browserWindow && browserWindow.requestAnimationFrame) {
        browserWindow.requestAnimationFrame(() => {
          browserWindow.requestAnimationFrame(callback);
        });
        return;
      }
      callback();
    };

    
    const run = () => {
      try {
        syncHighlightHeights();
        autoGrowTextareas();
        // Re-sync highlights after other growers so equal text/line space sticks.
        syncHighlightHeights();
        syncComparisonRowHeights();
        // Second pass after spacer rows + heights settle.
        syncComparisonRowHeights();
      } finally {
        layoutFramePendingRef.current = false;
        if (layoutFrameRequestedAgainRef.current) {
          layoutFrameRequestedAgainRef.current = false;
          scheduleReportTextareaLayout();
        }
      }
    };

    queueRun(run);
  };

  const autoResizeTextarea = (element: any) => {
    if (!element || !element.style) {
      return;
    }
    element.style.overflowY = "hidden";
    element.style.height = "auto";
    element.scrollTop = 0;
    let nextHeight = (element && element.scrollHeight) || 0;
    const surface: any = element.parentNode;
    const printText: any =
      surface && surface.querySelector
        ? surface.querySelector(".coach-report-preview__print-text")
        : null;
    if (printText) {
      const prevBottom = printText.style.bottom;
      const prevHeight = printText.style.height;
      const prevOverflow = printText.style.overflow;
      printText.style.bottom = "auto";
      printText.style.height = "auto";
      printText.style.overflow = "visible";
      const printHeight = printText.scrollHeight || 0;
      if (printHeight > nextHeight) {
        nextHeight = printHeight;
      }
      printText.style.bottom = prevBottom;
      printText.style.height = prevHeight;
      printText.style.overflow = prevOverflow;
    }
    element.style.height = nextHeight + 2 + "px";
    element.scrollTop = 0;
  };

  const highlightBodiesKey = report.highlightsStrengths
    .map((item) => `${item.title || ""}|${item.body || ""}`)
    .join("\0");
  const suggestionBodiesKey = report.suggestions
    .map((item) => item.body || "")
    .join("\0");
  const resourceTextKey = report.resources
    .map(
      (item) =>
        `${item.key || ""}|${item.title || ""}|${item.body || ""}|${
          item.whyChosen || ""
        }`
    )
    .join("\0");
  const comparisonLayoutKey = [
    report.expectations && report.expectations.rows
      ? report.expectations.rows
          .map((row) => `${row.label}|${row.studentScore}|${row.coachScore}`)
          .join(";")
      : "",
    report.relationalWarmth && report.relationalWarmth.rows
      ? report.relationalWarmth.rows
          .map((row) => `${row.label}|${row.studentScore}|${row.coachScore}`)
          .join(";")
      : "",
  ].join("\0");
  const growableTextKey = [
    highlightBodiesKey,
    suggestionBodiesKey,
    resourceTextKey,
    comparisonLayoutKey,
    report.overallSummary || "",
    report.parentFeedback && report.parentFeedback.summary
      ? report.parentFeedback.summary
      : "",
    report.finalNote || "",
    report.leaderCommitments || "",
    report.wiseLeadership && report.wiseLeadership.administratorComments
      ? report.wiseLeadership.administratorComments
      : "",
    report.wiseLeadership && report.wiseLeadership.administratorSignature
      ? report.wiseLeadership.administratorSignature
      : "",
  ].join("\0");

  React.useEffect(() => {
    scheduleReportTextareaLayout();
  }, [growableTextKey]);

  React.useLayoutEffect(() => {
    if (isLoadingPreview) {
      return;
    }
    syncHighlightHeights();
  }, [growableTextKey, isLoadingPreview, highlightBodiesKey]);

  React.useEffect(() => {
    if (isLoadingPreview) {
      return;
    }
    scheduleReportTextareaLayout();
  }, [isLoadingPreview]);

  React.useEffect(() => {
    const browserWindow: any = Function("return this")();
    if (!browserWindow || !browserWindow.addEventListener) {
      return;
    }
    const onResize = () => {
      scheduleReportTextareaLayout();
    };
    browserWindow.addEventListener("resize", onResize);
    return () => {
      if (browserWindow.removeEventListener) {
        browserWindow.removeEventListener("resize", onResize);
      }
    };
  }, []);

  const openPrintPreviewWindow = (
    targetWindow?: any,
    options?: { fast?: boolean }
  ) => {
    if (hasUnsavedChanges) {
      setSaveStatus("Please save report changes before printing.");
      if (targetWindow && !targetWindow.closed) {
        try {
          targetWindow.close();
        } catch (error) {
          // ignore
        }
      }
      return;
    }

    const previewRoot = printRootRef.current;
    if (!previewRoot) {
      return;
    }

    // Avoid bare `window`/`document` identifiers — live server TS has no DOM lib.
    const browserWindow: any = Function("return this")();
    const browserDocument: any = browserWindow && browserWindow.document;

    // Controlled in-page PDF overlay: closes only via the X button.
    if (!browserDocument || !browserDocument.body) {
      return;
    }

    // If a caller pre-opened a blank popup (legacy), close it — overlay replaces it.
    if (
      targetWindow &&
      targetWindow !== browserWindow &&
      !targetWindow.closed
    ) {
      try {
        targetWindow.close();
      } catch (error) {
        // ignore
      }
    }

    const existingOverlay = browserDocument.getElementById(
      "coach-report-print-overlay"
    );
    if (existingOverlay && existingOverlay.parentNode) {
      existingOverlay.parentNode.removeChild(existingOverlay);
    }

    const overlay: any = browserDocument.createElement("div");
    overlay.id = "coach-report-print-overlay";
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:2147483646;background:transparent;" +
      "display:flex;align-items:center;justify-content:center;padding:24px;" +
      "box-sizing:border-box;";

    const panel: any = browserDocument.createElement("div");
    panel.style.cssText =
      "background:#ffffff;width:min(1040px,100%);height:min(860px,100%);" +
      "max-width:100%;max-height:100%;border-radius:8px;" +
      "box-shadow:0 12px 40px rgba(15,23,42,0.35);display:flex;" +
      "flex-direction:column;overflow:hidden;";

    const bar: any = browserDocument.createElement("div");
    bar.style.cssText =
      "flex:0 0 auto;display:flex;justify-content:flex-end;align-items:center;" +
      "gap:8px;padding:8px 10px;border-bottom:1px solid #e2e8f0;background:#f8fafc;" +
      "position:relative;z-index:2;";

    const closeBtn: any = browserDocument.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", "Close PDF");
    closeBtn.setAttribute("title", "Close");
    closeBtn.innerHTML =
      '<span aria-hidden="true" style="display:block;line-height:1;font-size:28px;' +
      'font-weight:600;pointer-events:none;">&times;</span>';
    closeBtn.style.cssText =
      "appearance:none;align-items:center;background:transparent;border:none;" +
      "border-radius:8px;color:#1e293b;cursor:pointer;display:inline-flex;" +
      "height:40px;justify-content:center;line-height:1;padding:0;width:40px;" +
      "position:relative;z-index:3;";
    const closeOverlay = (event: any) => {
      if (event) {
        if (event.preventDefault) {
          event.preventDefault();
        }
        if (event.stopPropagation) {
          event.stopPropagation();
        }
      }
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    };
    closeBtn.addEventListener("click", closeOverlay);

    const iframe: any = browserDocument.createElement("iframe");
    iframe.title = "Coach report PDF preview";
    iframe.style.cssText =
      "flex:1 1 auto;width:100%;height:100%;border:0;background:#ffffff;";

    bar.appendChild(closeBtn);
    panel.appendChild(bar);
    panel.appendChild(iframe);
    overlay.appendChild(panel);
    browserDocument.body.appendChild(overlay);

    const printWindow = iframe.contentWindow;
    if (!printWindow || !printWindow.document) {
      closeOverlay(null);
      if (browserWindow.alert) {
        browserWindow.alert("Could not open the print PDF preview.");
      }
      return;
    }

    // Equalize comparison <tr> heights in the live preview before cloning.
    try {
      syncComparisonRowHeights();
    } catch (error) {
      console.error("Preview comparison row sync failed", error);
    }

    // Only report styles — do NOT copy app CSS (evaluation-pagess overflow:hidden clips print).
    const styleNodes: any[] = Array.prototype.slice.call(
      previewRoot.querySelectorAll("style")
    );
    const reportStyles = styleNodes
      .map((node: any) => node.outerHTML || "")
      .join("\n");

    const escapeHtml = (value: string) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const printTitle = escapeHtml(report.schoolName || "Coach");
    const footerBrandText = String(footerBrandDisplay || "");
    const escapeCssContent = (value: string) =>
      String(value || "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')
        .replace(/\r?\n/g, " ");
    const footerBrandCss = escapeCssContent(footerBrandText);

    const clone: any = previewRoot.cloneNode(true);
    const toolbar = clone.querySelector(".coach-report-preview__toolbar");
    if (toolbar && toolbar.parentNode) {
      toolbar.parentNode.removeChild(toolbar);
    }

    const textareas = clone.querySelectorAll(
      "textarea.coach-report-preview__edit-field"
    );
    Array.prototype.forEach.call(textareas, (textarea: any) => {
      const textareaWrap: any = textarea.closest(
        ".coach-report-preview__editable-field-wrap"
      );
      const printText: any = textareaWrap
        ? textareaWrap.querySelector(".coach-report-preview__print-text") ||
          textareaWrap.nextElementSibling
        : textarea.nextElementSibling;
      if (
        printText &&
        printText.classList.contains("coach-report-preview__print-text")
      ) {
        printText.innerHTML = boldDynamicScoresHtml(textarea.value || "");
        printText.style.display = "block";
        printText.style.position = "static";
        printText.style.height = "auto";
        printText.style.minHeight = "0";
        printText.style.opacity = "1";
        printText.style.gridArea = "auto";
        if (textareaWrap && textareaWrap.parentNode) {
          if (textareaWrap.contains(printText)) {
            textareaWrap.parentNode.insertBefore(printText, textareaWrap);
          }
          textareaWrap.parentNode.removeChild(textareaWrap);
        } else if (textarea.parentNode) {
          textarea.parentNode.removeChild(textarea);
        }
      } else {
        const mirror = browserDocument.createElement("div");
        mirror.className = textarea.className
          .replace("coach-report-preview__edit-field", "")
          .trim();
        mirror.classList.add("coach-report-preview__print-text");
        mirror.innerHTML = boldDynamicScoresHtml(textarea.value || "");
        if (textarea.parentNode) {
          textarea.parentNode.replaceChild(mirror, textarea);
        }
      }
    });

    // Modal-only pages (e.g. standalone Parent Feedback) must not appear in PDF.
    const screenOnlyNodes = clone.querySelectorAll(
      ".coach-report-preview__screen-only-from-modal"
    );
    Array.prototype.forEach.call(screenOnlyNodes, (node: any) => {
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });

    // Keep in-flow footers; stamp real Page X of Y (CSS counters show 0 of 0).
    const printPages = clone.querySelectorAll(".coach-report-preview__page");
    const printPageTotal = printPages.length || 8;
    Array.prototype.forEach.call(printPages, (pageEl: any, index: number) => {
      let footer = pageEl.querySelector(".coach-report-preview__page-footer");
      if (!footer) {
        footer = browserDocument.createElement("div");
        footer.className = "coach-report-preview__page-footer";
        const brand = browserDocument.createElement("div");
        brand.className = "coach-report-preview__footer-brand";
        brand.textContent = footerBrandText;
        const pageLabel = browserDocument.createElement("div");
        pageLabel.className = "coach-report-preview__footer-page";
        footer.appendChild(brand);
        footer.appendChild(pageLabel);
        pageEl.appendChild(footer);
      }
      const brandEl = footer.querySelector(".coach-report-preview__footer-brand");
      if (brandEl) {
        brandEl.textContent = footerBrandText;
      }
      const pageLabelEl = footer.querySelector(
        ".coach-report-preview__footer-page"
      );
      if (pageLabelEl) {
        pageLabelEl.textContent =
          "Page " + (index + 1) + " of " + printPageTotal;
      }
    });

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${printTitle} Feedback Report</title>
    ${reportStyles}
    <style>
      html, body, .coach-report-print-window {
        background: #ffffff !important;
        box-sizing: border-box !important;
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
        height: auto !important;
        margin: 0 !important;
        max-height: none !important;
        max-width: 100% !important;
        overflow: visible !important;
        padding: 0 !important;
        width: 100% !important;
      }
      body {
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
        padding: 0 !important;
      }
      .coach-report-preview,
      .coach-report-preview * {
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
      }
      .coach-report-preview {
        box-sizing: border-box !important;
        max-width: 100% !important;
        min-width: 0 !important;
        overflow: visible !important;
        width: 100% !important;
      }
      .coach-report-preview__toolbar,
      .coach-report-preview__edit-field,
      .coach-report-preview__char-limit,
      .coach-report-preview__screen-only-from-modal {
        display: none !important;
      }
      .coach-report-preview__print-only-from-modal {
        display: block !important;
      }
      .coach-report-preview__editable-field-surface {
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        position: static !important;
      }
      .coach-report-preview__editable-field-surface
        > .coach-report-preview__print-text {
        bottom: auto !important;
        display: block !important;
        grid-area: auto !important;
        height: auto !important;
        left: auto !important;
        position: static !important;
        right: auto !important;
        top: auto !important;
        width: 100% !important;
      }
      .coach-report-preview__print-text {
        display: block !important;
      }
      .coach-report-preview__dynamic-score,
      .coach-report-preview strong.coach-report-preview__dynamic-score {
        font-style: inherit !important;
        font-weight: 700 !important;
      }
      .coach-report-preview__resource-why-chosen
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__print-text,
      .coach-report-preview__resource-why-chosen .coach-report-preview__resource-why-chosen-text {
        display: inline !important;
      }
      .coach-report-preview__resource-why-chosen
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
        display: none !important;
      }
      /* In-flow / fixed footers: page numbers only work in @page margin boxes. */
      .coach-report-preview__page-footer,
      .coach-report-print-fixed-footer,
      .coach-report-sheet-footer {
        display: none !important;
      }
      .coach-report-preview__page,
      .coach-report-preview__page--fill {
        background: #ffffff !important;
        box-sizing: border-box !important;
        display: block !important;
        height: auto !important;
        margin: 0 !important;
        max-height: none !important;
        max-width: 100% !important;
        min-height: 0 !important;
        overflow: visible !important;
        padding: 0 !important;
        position: relative !important;
        width: 100% !important;
      }
      .coach-report-preview__page-content--fill {
        display: block !important;
        overflow: visible !important;
      }
      .coach-report-preview__running-header {
        display: flex !important;
        flex: 0 0 auto !important;
        padding: 8px 20px !important;
      }
      .coach-report-preview__report-header,
      .coach-report-preview__data-section-banner {
        flex: 0 0 auto !important;
        padding-left: 20px !important;
        padding-right: 20px !important;
        padding-top: 6px !important;
      }
      .coach-report-preview__header-top {
        margin-bottom: 6px !important;
      }
      .coach-report-preview__header-meta {
        padding: 6px 0 6px !important;
      }
      .coach-report-preview__page-content {
        flex: none !important;
        max-width: 100% !important;
        overflow: visible !important;
        padding: 6px 20px 0 !important;
      }
      .coach-report-preview__section-header {
        margin-top: 8px !important;
      }
      .coach-report-preview__page--expectations .coach-report-preview__page-content {
        padding-top: 8px !important;
      }
      .coach-report-preview__page--expectations .coach-report-preview__score-legend {
        margin-bottom: 8px !important;
        margin-top: 0 !important;
      }
      .coach-report-preview__comparison-pair-headers .coach-report-preview__section-header {
        margin: 2px 0 8px !important;
      }
      .coach-report-preview__editable-text--summary,
      .coach-report-preview__editable-text--highlight,
      .coach-report-preview__editable-text--suggestion,
      .coach-report-preview__editable-text--resource-title,
      .coach-report-preview__editable-text--resource-body,
      .coach-report-preview__editable-text--resource-why-chosen,
      .coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion,
      .coach-report-preview__suggestion-item .coach-report-preview__print-text {
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
      }
      .coach-report-preview__kpi-grid {
        margin-bottom: 8px !important;
        margin-top: 6px !important;
      }
      .coach-report-preview__kpi-card {
        padding: 6px 6px !important;
      }
      .coach-report-preview__highlight-card {
        min-height: 0 !important;
        padding: 8px 11px !important;
      }
      .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field,
      .coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field,
      .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
        background: transparent !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        margin: 0 !important;
        outline: none !important;
        padding: 0 !important;
        resize: none !important;
      }
      .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field,
      .coach-report-preview__editable-text--resource-title.coach-report-preview__print-text,
      .coach-report-preview__resource-title-big {
        color: #1a2a3a !important;
        display: block !important;
        font-size: 9pt !important;
        font-weight: 700 !important;
        line-height: 1.35 !important;
        margin: 0 0 0px !important;
      }
      .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field {
        min-height: 0 !important;
      }
      .coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field {
        color: #334155 !important;
        font-size: 7.5pt !important;
        line-height: 1.55 !important;
      }
      .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
        color: #64748b !important;
        display: inline !important;
        font-size: 7.5pt !important;
        font-style: italic !important;
        font-weight: 400 !important;
        line-height: 1.55 !important;
        width: auto !important;
      }
      .coach-report-preview__editable-text--resource-body.coach-report-preview__print-text,
      .coach-report-preview__resource-desc {
        color: #334155 !important;
        display: block !important;
        font-size: 7.5pt !important;
        line-height: 1.55 !important;
        margin: 0 !important;
      }
      .coach-report-preview__resource-why-chosen {
        border-top: 1px solid #e2e8f0 !important;
        color: #64748b !important;
        font-size: 7.5pt !important;
        font-style: italic !important;
        line-height: 1.55 !important;
        margin-top: 0px !important;
        padding-top: 5px !important;
      }
      .coach-report-preview__resource-why-chosen-para {
        margin: 0 !important;
        padding: 0 !important;
      }
      .coach-report-preview__resource-why-chosen-label,
      .coach-report-preview__resource-why-chosen strong {
        color: #1a2a3a !important;
        display: inline !important;
        float: none !important;
        font-style: italic !important;
        font-weight: 700 !important;
        margin: 0 !important;
      }
      .coach-report-preview__resource-why-chosen-text,
      .coach-report-preview__resource-why-chosen
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__print-text {
        border: none !important;
        color: #64748b !important;
        display: inline !important;
        font-style: italic !important;
        font-weight: 400 !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .coach-report-preview__resource-why-chosen .coach-report-preview__editable-field-wrap {
        display: inline !important;
        overflow: visible !important;
        width: auto !important;
      }
      .coach-report-preview__resource-card-big {
        background: #ffffff !important;
        border: 1px solid #e2e8f0 !important;
        border-left-width: 4px !important;
        border-left-style: solid !important;
        border-radius: 10px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
        overflow: hidden !important;
        padding: 10px 14px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .coach-report-preview__resource-card-big--book {
        border-left-color: #1a3a5c !important;
      }
      .coach-report-preview__resource-card-big--article {
        border-left-color: #0d7a6e !important;
      }
      .coach-report-preview__resource-card-big--podcast {
        border-left-color: #b45309 !important;
      }
      .coach-report-preview__resource-type-badge {
        border-radius: 20px !important;
        display: inline-block !important;
        font-size: 6.5pt !important;
        font-weight: 700 !important;
        letter-spacing: 0.8px !important;
        line-height: 1.2 !important;
        margin-bottom: 6px !important;
        padding: 2px 8px !important;
        text-transform: uppercase !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .coach-report-preview__resource-card-big--book .coach-report-preview__resource-type-badge {
        background: #dce8f5 !important;
        color: #1a3a5c !important;
      }
      .coach-report-preview__resource-card-big--article .coach-report-preview__resource-type-badge {
        background: #d0f0ec !important;
        color: #0d7a6e !important;
      }
      .coach-report-preview__resource-card-big--podcast .coach-report-preview__resource-type-badge {
        background: #fef3c7 !important;
        color: #b45309 !important;
      }
      .coach-report-preview__resource-list {
        gap: 10px !important;
      }
      .coach-report-preview__resource-title-big {
        color: #1a2a3a !important;
        font-size: 9pt !important;
        font-weight: 700 !important;
        line-height: 1.35 !important;
        margin: 0 0 5px !important;
      }
      .coach-report-preview__editable-text--resource-body.coach-report-preview__print-text,
      .coach-report-preview__resource-desc {
        color: #334155 !important;
        display: block !important;
        font-size: 7.5pt !important;
        line-height: 1.55 !important;
        margin: 0 !important;
      }
      .coach-report-preview__resource-card-actions {
        display: none !important;
        margin-bottom: 0 !important;
      }
      .coach-report-preview__resource-remove {
        background: transparent !important;
        border: none !important;
        color: #8a97ab !important;
        cursor: pointer !important;
        display: none !important;
        font-size: 12px !important;
        font-weight: 700 !important;
        height: auto !important;
        line-height: 1.2 !important;
        padding: 0 !important;
      }
      .coach-report-preview__resource-remove:hover {
        color: #c23a3a !important;
      }
      .coach-report-preview__resource-remove-icon {
        display: none !important;
      }
      .coach-report-preview__resource-remove-label {
        display: inline !important;
      }
      .coach-report-preview__editable-text--resource-title + .coach-report-preview__char-limit,
      .coach-report-preview__editable-text--resource-body + .coach-report-preview__char-limit,
      .coach-report-preview__editable-text--resource-why-chosen + .coach-report-preview__char-limit {
        margin-top: 1px !important;
      }
      .coach-report-preview__overall-summary-box {
        margin-bottom: 0 !important;
      }
      .coach-report-preview__highlight-grid {
        gap: 9px !important;
        margin-bottom: 10px !important;
      }
      .coach-report-preview__connectedness-block {
        break-before: auto !important;
        break-inside: auto !important;
        margin-bottom: 0 !important;
        margin-top: 6px !important;
        page-break-before: auto !important;
        page-break-inside: auto !important;
      }
      .coach-report-preview__connectedness-footer-note {
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        break-before: avoid !important;
        margin-bottom: 0 !important;
        margin-top: 4px !important;
        page-break-before: avoid !important;
        padding: 0 !important;
      }
      .coach-report-preview__resource-list,
      .coach-report-preview__suggestion-list {
        gap: 20px !important;
        margin-bottom: 0 !important;
      }
      .coach-report-preview__suggestion-list {
        gap: 5px !important;
      }
      .coach-report-preview__resource-card-big,
      .coach-report-preview__suggestion-item,
      .coach-report-preview__summary-box,
      .coach-report-preview__data-table-wrap,
      .coach-report-preview__comparison-table-wrap,
      .coach-report-preview__comment-section,
      .coach-report-preview__comment-pair-table,
      .coach-report-preview__admin-comments-card,
      .coach-report-preview__pull-quote,
      .pull-quote {
        margin-bottom: 0 !important;
      }
      .coach-report-preview__wise-section {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      .coach-report-preview__wise-section .coach-report-preview__section-header {
        break-after: avoid !important;
        page-break-after: avoid !important;
      }
      .coach-report-preview__connectedness-block .coach-report-preview__section-header {
        margin-bottom: 4px !important;
        margin-top: 0 !important;
      }
      .coach-report-preview__connectedness-block .coach-report-preview__intro-text {
        margin-bottom: 6px !important;
      }
      .coach-report-preview__data-table-wrap + .coach-report-preview__summary-box {
        margin-top: 14px !important;
        padding-top: 18px !important;
      }
      .coach-report-preview__final-note-box + .coach-report-preview__section-header {
        margin-top: 16px !important;
      }
      .coach-report-preview__header-top {
        align-items: flex-start !important;
      }
      .coach-report-preview__school-logo {
        border-radius: 10px !important;
        height: 52px !important;
        width: 52px !important;
      }
      .coach-report-preview__data-table,
      .coach-report-preview__comparison-table {
        table-layout: fixed !important;
        width: 100% !important;
      }
      .coach-report-preview__comparison-table-wrap,
      .coach-report-preview__comparison-block,
      .coach-report-preview__two-column-grid--comparison,
      .coach-report-preview__data-table-wrap {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }
      /* Same desktop 2-col layout in print (table cells avoid blank header-only pages). */
      .coach-report-preview__two-column-grid--comparison {
        border-collapse: separate !important;
        border-spacing: 12px 0 !important;
        display: table !important;
        table-layout: fixed !important;
        width: 100% !important;
      }
      .coach-report-preview__comparison-block {
        display: table-cell !important;
        vertical-align: top !important;
        width: 50% !important;
      }
      .coach-report-preview__comparison-block .coach-report-preview__summary-box {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }
      .coach-report-preview__score-legend,
      .coach-report-preview__section-header {
        break-after: avoid !important;
        margin-top: 8px !important;
        page-break-after: avoid !important;
      }
      .coach-report-preview__page--expectations .coach-report-preview__score-legend {
        margin-bottom: 8px !important;
        margin-top: 0 !important;
      }
      .coach-report-preview__comparison-pair-headers .coach-report-preview__section-header {
        margin: 2px 0 8px !important;
      }
      .coach-report-preview__running-header {
        break-after: avoid !important;
        page-break-after: avoid !important;
      }
      .coach-report-preview__page-content {
        break-before: avoid !important;
        page-break-before: avoid !important;
      }
      .coach-report-preview__comment-summaries-keep {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      .coach-report-preview__comment-pair-questions,
      .coach-report-preview__comment-summaries-headers {
        break-after: avoid !important;
        page-break-after: avoid !important;
      }
      .coach-report-preview__comparison-table tr {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      /* Do not repeat Survey Item / Average headers on each printed sheet. */
      .coach-report-preview__comparison-table thead,
      .coach-report-preview__comparison-table tfoot,
      .coach-report-preview__data-table thead,
      .coach-report-preview__data-table tfoot {
        display: table-row-group !important;
      }
      .coach-report-preview__comparison-table-wrap,
      .coach-report-preview__data-table-wrap {
        border-radius: 0 !important;
        overflow: visible !important;
      }
      .coach-report-preview__comparison-table tbody tr[data-comparison-spacer] td {
        border-bottom-color: transparent !important;
      }
      .coach-report-preview__comparison-table th,
      .coach-report-preview__comparison-table td {
        box-sizing: border-box !important;
        font-size: 6.8pt !important;
        line-height: 1.4 !important;
        padding: 5px 8px !important;
        vertical-align: middle !important;
      }
      .coach-report-preview__comparison-table thead th,
      .coach-report-preview__comparison-table tfoot td {
        font-size: 7pt !important;
      }
      .coach-report-preview__comparison-table tbody tr:nth-child(odd) {
        background: #ffffff !important;
      }
      .coach-report-preview__comparison-table tbody tr:nth-child(even) {
        background: #f8fafc !important;
      }
      .coach-report-preview__score-meter {
        align-items: center !important;
        display: inline-flex !important;
        gap: 4px !important;
        justify-content: flex-end !important;
        white-space: nowrap !important;
      }
      .coach-report-preview__comparison-table .coach-report-preview__score-meter {
        display: flex !important;
        justify-content: center !important;
        width: 100% !important;
      }
      .coach-report-preview__score-meter-value {
        font-size: 7pt !important;
        font-weight: 700 !important;
        line-height: 1 !important;
        min-width: 2em !important;
        text-align: right !important;
      }
      .coach-report-preview__comparison-table .coach-report-preview__score-meter-value {
        text-align: center !important;
      }
      .coach-report-preview__score-meter-track {
        border-radius: 999px !important;
        flex: 0 0 12px !important;
        height: 5px !important;
        overflow: hidden !important;
        width: 12px !important;
      }
      .coach-report-preview__comparison-table--pair th.coach-report-preview__comparison-pair-gap,
      .coach-report-preview__comparison-table--pair td.coach-report-preview__comparison-pair-gap {
        background: #ffffff !important;
        border: none !important;
        padding: 0 !important;
        width: 12px !important;
      }
      .coach-report-preview__comparison-table--pair thead th.coach-report-preview__comparison-pair-gap,
      .coach-report-preview__comparison-table--pair tfoot td.coach-report-preview__comparison-pair-gap {
        background: #ffffff !important;
      }
      .coach-report-preview__comparison-pair-headers,
      .coach-report-preview__comparison-pair-summaries {
        border-collapse: separate !important;
        border-spacing: 12px 0 !important;
        display: table !important;
        table-layout: fixed !important;
        width: 100% !important;
      }
      .coach-report-preview__comparison-pair-summaries {
        margin-left: -12px !important;
        margin-right: -12px !important;
        margin-top: 12px !important;
        width: calc(100% + 24px) !important;
      }
      .coach-report-preview__comparison-pair-headers > *,
      .coach-report-preview__comparison-pair-summaries > * {
        display: table-cell !important;
        vertical-align: top !important;
        width: 50% !important;
      }
      .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box {
        padding: 18px 14px 12px !important;
        text-align: left !important;
        vertical-align: top !important;
      }
      .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box-label {
        margin-left: 0 !important;
        margin-top: 0 !important;
        padding-left: 0 !important;
        text-align: left !important;
      }
      .coach-report-preview__score-meter-fill {
        border-radius: 999px !important;
        display: block !important;
        height: 100% !important;
        max-width: 100% !important;
      }
      .coach-report-preview__score-meter--coach .coach-report-preview__score-meter-value {
        color: #000000 !important;
      }
      .coach-report-preview__score-meter--coach .coach-report-preview__score-meter-track {
        background: rgba(0, 0, 0, 0.18) !important;
      }
      .coach-report-preview__score-meter--coach .coach-report-preview__score-meter-fill {
        background: #000000 !important;
      }
      .coach-report-preview__comparison-table td.coach-report-preview__score-col,
      .coach-report-preview__comparison-table tfoot td.coach-report-preview__score-col {
        text-align: center !important;
        white-space: nowrap !important;
        width: 86px !important;
      }
      .coach-report-preview__data-table thead th:last-child,
      .coach-report-preview__data-table td:last-child,
      .coach-report-preview__data-table tfoot td:last-child,
      .coach-report-preview__data-table th.coach-report-preview__score-heading,
      .coach-report-preview__data-table td.coach-report-preview__score-col {
        text-align: center !important;
        white-space: nowrap !important;
        width: 100px !important;
      }
      .coach-report-preview__connectedness-block .coach-report-preview__data-table thead th:last-child,
      .coach-report-preview__connectedness-block .coach-report-preview__data-table td:last-child,
      .coach-report-preview__connectedness-block .coach-report-preview__data-table tfoot td:last-child,
      .coach-report-preview__connectedness-block .coach-report-preview__data-table th.coach-report-preview__score-heading,
      .coach-report-preview__connectedness-block .coach-report-preview__data-table td.coach-report-preview__score-col {
        width: 200px !important;
      }
      .coach-report-preview__score-cell {
        gap: 5px !important;
        justify-content: center !important;
      }
      .coach-report-preview__connectedness-score-text--message {
        font-size: 11px !important;
        line-height: 1.3 !important;
        max-width: none !important;
        padding-right: 0 !important;
        text-align: center !important;
        white-space: nowrap !important;
        word-break: normal !important;
      }
      .coach-report-preview__score-bar-wrap {
        border-radius: 3px !important;
        flex: 0 0 40px !important;
        height: 5px !important;
        width: 40px !important;
      }
      .coach-report-preview__data-table--parent-feedback .coach-report-preview__score-bar,
      .coach-report-preview__data-table-wrap--parent-feedback .coach-report-preview__score-bar {
        background: linear-gradient(90deg, var(--green) 0%, #34d399 100%) !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .coach-report-preview__data-table tfoot {
        display: table-row-group !important;
      }
      .coach-report-preview__data-table thead th,
      .coach-report-preview__data-table tfoot td {
        line-height: 1.4 !important;
        padding: 11px 14px !important;
      }
      .coach-report-preview__suggestion-list {
        break-inside: auto !important;
        gap: 5px !important;
        page-break-inside: auto !important;
      }
      .coach-report-preview__suggestion-item:not(:first-child) {
        margin-top: 0 !important;
      }
      .coach-report-preview__comment-summaries-columns {
        align-items: start !important;
        display: grid !important;
        gap: 14px !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
      .coach-report-preview__comment-section {
        margin-bottom: 0 !important;
        margin-top: 10px !important;
      }
      .coach-report-preview__comment-summaries-col > .coach-report-preview__comment-section:first-child {
        margin-top: 0 !important;
      }
      .coach-report-preview__comment-item,
      .coach-report-preview__comment-text,
      .coach-report-preview__comment-title,
      .coach-report-preview__comment-body,
      .coach-report-preview__comment-num {
        line-height: 1.25 !important;
      }
      .coach-report-preview__comment-summaries-headers {
        border-collapse: separate !important;
        border-spacing: 14px 0 !important;
        display: table !important;
        margin-bottom: 10px !important;
        table-layout: fixed !important;
        width: 100% !important;
      }
      .coach-report-preview__comment-summaries-headers > * {
        display: table-cell !important;
        vertical-align: top !important;
        width: 50% !important;
      }
      .coach-report-preview__comment-summaries-headers .coach-report-preview__section-header {
        align-items: center !important;
        display: flex !important;
        flex-direction: row !important;
      }
      .coach-report-preview__comment-summaries-headers .coach-report-preview__section-bar {
        align-self: stretch !important;
        display: block !important;
        flex: 0 0 4px !important;
        width: 4px !important;
      }
      .coach-report-preview__comment-placeholder-box {
        align-items: center !important;
        box-sizing: border-box !important;
        height: auto !important;
        max-height: none !important;
        min-height: 52px !important;
        overflow: visible !important;
        padding-top: 12px !important;
        padding-bottom: 12px !important;
        position: static !important;
        white-space: normal !important;
        word-break: break-word !important;
      }
      .coach-report-preview__comment-question {
        margin-bottom: 5px !important;
      }
      .coach-report-preview__comparison-pair-headers,
      .coach-report-preview__comparison-pair-summaries {
        border-collapse: separate !important;
        border-spacing: 12px 0 !important;
        display: table !important;
        table-layout: fixed !important;
        width: 100% !important;
      }
      .coach-report-preview__comparison-pair-summaries {
        margin-left: -12px !important;
        margin-right: -12px !important;
        margin-top: 12px !important;
        width: calc(100% + 24px) !important;
      }
      .coach-report-preview__comparison-pair-headers > *,
      .coach-report-preview__comparison-pair-summaries > * {
        display: table-cell !important;
        vertical-align: top !important;
        width: 50% !important;
      }
      .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box {
        padding: 18px 14px 12px !important;
        text-align: left !important;
        vertical-align: top !important;
      }
      .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box-label {
        margin-left: 0 !important;
        margin-top: 0 !important;
        padding-left: 0 !important;
        text-align: left !important;
      }
      .coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-gap,
      .coach-report-preview__comparison-table--pair th.coach-report-preview__comparison-pair-gap,
      .coach-report-preview__comparison-table--pair td.coach-report-preview__comparison-pair-gap {
        width: 12px !important;
      }
      .coach-report-preview__connectedness-block {
        margin-top: 6px !important;
      }
      .coach-report-preview__connectedness-block .coach-report-preview__intro-text {
        margin-bottom: 6px !important;
      }
      .coach-report-preview__suggestion-item {
        background: #f8fafc !important;
        border: 1px solid #e2e8f0 !important;
        border-left: 4px solid var(--coach-report-accent) !important;
        border-radius: 10px !important;
        overflow: hidden !important;
        padding: 8px 12px 8px 10px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .coach-report-preview__suggestion-item::before {
        content: none !important;
        display: none !important;
      }
      .coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion,
      .coach-report-preview__suggestion-item .coach-report-preview__print-text,
      .coach-report-preview__editable-text--suggestion {
        font-size: 7.5pt !important;
        height: auto !important;
        line-height: 1.35 !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
      }
      .coach-report-preview__wise-layout {
        align-items: stretch !important;
        -webkit-column-break-inside: avoid !important;
        break-inside: avoid !important;
        box-sizing: border-box !important;
        display: grid !important;
        gap: 16px !important;
        grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr) !important;
        max-width: 100% !important;
        overflow: visible !important;
        page-break-inside: avoid !important;
        width: 100% !important;
      }
      .coach-report-preview__wise-grid-column,
      .coach-report-preview__wise-side-column {
        -webkit-column-break-inside: avoid !important;
        break-inside: avoid !important;
        display: flex !important;
        max-width: 100% !important;
        min-width: 0 !important;
        overflow: visible !important;
        page-break-inside: avoid !important;
      }
      .coach-report-preview__leader-grid,
      .coach-report-preview__leader-panel--chart,
      .coach-report-preview__grid-placement-card {
        -webkit-column-break-inside: avoid !important;
        break-inside: avoid !important;
        max-width: 100% !important;
        overflow: visible !important;
        page-break-inside: avoid !important;
        width: 100% !important;
      }
      .coach-report-preview__leader-grid .wlg-chart-wrap {
        aspect-ratio: 1 / 1 !important;
        -webkit-column-break-inside: avoid !important;
        break-inside: avoid !important;
        max-height: none !important;
        max-width: 100% !important;
        overflow: visible !important;
        page-break-inside: avoid !important;
        position: relative !important;
        width: 100% !important;
      }
      .coach-report-preview__leader-grid .wlg-chart-wrap svg {
        display: block !important;
        height: 100% !important;
        left: 0 !important;
        position: absolute !important;
        top: 0 !important;
        width: 100% !important;
      }
      .coach-report-preview__grid-placement-card {
        box-sizing: border-box !important;
        min-height: 0 !important;
        padding: 12px !important;
      }
      .coach-report-preview__admin-comments-card,
      .coach-report-preview__admin-comments-card--fill {
        background: #f8fafc !important;
        border: 1px solid #e2e8f0 !important;
        border-left: 4px solid var(--coach-report-accent) !important;
        border-radius: 10px !important;
        box-sizing: border-box !important;
        max-width: 100% !important;
        min-width: 0 !important;
        overflow: hidden !important;
        padding: 12px 16px 12px 16px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        width: 100% !important;
      }
      .coach-report-preview__admin-comments-card::before {
        content: none !important;
        display: none !important;
      }
      .coach-report-preview__admin-comments-body-wrap,
      .coach-report-preview__admin-comments-body,
      .coach-report-preview__admin-comments-signature,
      .coach-report-preview__admin-comments-body.coach-report-preview__print-text,
      .coach-report-preview__admin-comments-signature.coach-report-preview__print-text {
        box-sizing: border-box !important;
        flex: 0 0 auto !important;
        height: auto !important;
        max-width: 100% !important;
        min-height: 0 !important;
        min-width: 0 !important;
        overflow: hidden !important;
        width: 100% !important;
      }
      @media print {
        html, body, .coach-report-print-window {
          height: auto !important;
          margin: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          padding: 0 !important;
          width: 100% !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          padding: 0 !important;
        }
        /* Page breaks between logical sections; @page margin boxes hold the footer. */
        .coach-report-preview__page,
        .coach-report-preview__page--fill {
          break-after: page !important;
          box-sizing: border-box !important;
          display: block !important;
          height: auto !important;
          margin: 0 !important;
          max-height: none !important;
          max-width: 100% !important;
          min-height: 0 !important;
          overflow: visible !important;
          page-break-after: always !important;
          padding: 0 !important;
          position: relative !important;
          width: 100% !important;
        }
        .coach-report-preview__page:last-child,
        .coach-report-preview__page--fill:last-child {
          break-after: auto !important;
          page-break-after: auto !important;
        }
        .coach-report-preview__page-footer,
        .coach-report-print-fixed-footer,
        .coach-report-sheet-footer {
          display: none !important;
        }
        /* Exact same header as ReportGenerateModal preview pages. */
        .coach-report-preview__running-header {
          align-items: center !important;
          background: var(--coach-report-accent) !important;
          color: #ffffff !important;
          display: flex !important;
          flex: 0 0 auto !important;
          justify-content: space-between !important;
          padding: 8px 20px !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .coach-report-preview__running-header-left {
          color: rgba(255, 255, 255, 0.8) !important;
          font-size: 8pt !important;
          font-weight: 600 !important;
        }
        .coach-report-preview__running-header-right {
          color: rgba(255, 255, 255, 0.45) !important;
          font-size: 7.5pt !important;
        }
        .coach-report-preview__page-content,
        .coach-report-preview__page-content--fill {
          box-sizing: border-box !important;
          display: block !important;
          overflow: visible !important;
          padding: 6px 20px 0 !important;
        }
        .coach-report-preview__section-header {
          margin-top: 8px !important;
        }
        /* Top spacing only on Expectations / Relational Warmth print page. */
        .coach-report-preview__page--expectations .coach-report-preview__page-content {
          padding-top: 8px !important;
        }
        .coach-report-preview__page--expectations .coach-report-preview__score-legend {
          margin-bottom: 8px !important;
          margin-top: 0 !important;
        }
        .coach-report-preview__comparison-pair-headers .coach-report-preview__section-header {
          margin: 2px 0 8px !important;
        }
        .coach-report-preview__report-header,
        .coach-report-preview__data-section-banner {
          padding-left: 20px !important;
          padding-right: 20px !important;
        }
        /* Do not repeat Survey Item / Average headers when a table splits across pages. */
        .coach-report-preview__comparison-table thead,
        .coach-report-preview__comparison-table tfoot,
        .coach-report-preview__data-table thead,
        .coach-report-preview__data-table tfoot {
          display: table-row-group !important;
        }
        .coach-report-preview__comparison-table-wrap,
        .coach-report-preview__data-table-wrap {
          border-radius: 0 !important;
          overflow: visible !important;
        }
        /* Never split graphs/charts — restore full chart width (grid, not 50/50 table). */
        .coach-report-preview__wise-section,
        .coach-report-preview__wise-layout,
        .coach-report-preview__wise-grid-column,
        .coach-report-preview__wise-side-column,
        .coach-report-preview__leader-panel--chart,
        .coach-report-preview__leader-grid,
        .coach-report-preview__leader-grid .wlg-chart-wrap,
        .coach-report-preview__leader-grid .wlg-chart-wrap svg,
        .coach-report-preview__grid-placement-card {
          -webkit-column-break-inside: avoid !important;
          break-inside: avoid !important;
          overflow: visible !important;
          page-break-inside: avoid !important;
        }
        .coach-report-preview__wise-layout {
          align-items: stretch !important;
          display: grid !important;
          gap: 16px !important;
          grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr) !important;
          width: 100% !important;
        }
        .coach-report-preview__wise-grid-column,
        .coach-report-preview__wise-side-column {
          display: flex !important;
          max-width: 100% !important;
          min-width: 0 !important;
          width: auto !important;
        }
        .coach-report-preview__leader-grid {
          margin: 0 !important;
          max-width: 100% !important;
          width: 100% !important;
        }
        .coach-report-preview__leader-grid .wlg-chart-wrap {
          aspect-ratio: 1 / 1 !important;
          height: auto !important;
          max-height: none !important;
          max-width: 100% !important;
          width: 100% !important;
        }
        .coach-report-preview__kpi-grid,
        .coach-report-preview__highlight-grid,
        .coach-report-preview__highlight-card,
        .coach-report-preview__overall-summary-box,
        .coach-report-preview__suggestion-item,
        .coach-report-preview__summary-box,
        .coach-report-preview__comment-section,
        .coach-report-preview__resource-card-big,
        .coach-report-preview__data-table-wrap,
        .coach-report-preview__admin-comments-card,
        .coach-report-preview__admin-comments-card--fill {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        .coach-report-preview__connectedness-block {
          break-before: auto !important;
          break-inside: auto !important;
          margin-bottom: 0 !important;
          margin-top: 6px !important;
          page-break-before: auto !important;
          page-break-inside: auto !important;
        }
        .coach-report-preview__connectedness-footer-note {
          border: none !important;
          break-before: avoid !important;
          margin-bottom: 0 !important;
          margin-top: 4px !important;
          page-break-before: avoid !important;
          padding: 0 !important;
        }
        .coach-report-preview__resource-card-big,
        .coach-report-preview__suggestion-item,
        .coach-report-preview__summary-box,
        .coach-report-preview__highlight-card,
        .coach-report-preview__overall-summary-box,
        .coach-report-preview__data-table-wrap,
        .coach-report-preview__comparison-table-wrap,
        .coach-report-preview__comment-section,
        .coach-report-preview__admin-comments-card,
        .coach-report-preview__pull-quote,
        .pull-quote,
        .coach-report-preview__resource-list,
        .coach-report-preview__suggestion-list,
        .coach-report-preview__highlight-grid {
          margin-bottom: 0 !important;
        }
        .coach-report-preview__data-table thead th:last-child,
        .coach-report-preview__data-table td:last-child,
        .coach-report-preview__data-table tfoot td:last-child,
        .coach-report-preview__data-table th.coach-report-preview__score-heading,
        .coach-report-preview__data-table td.coach-report-preview__score-col {
          width: 100px !important;
          white-space: nowrap !important;
        }
        .coach-report-preview__connectedness-block .coach-report-preview__data-table thead th:last-child,
        .coach-report-preview__connectedness-block .coach-report-preview__data-table td:last-child,
        .coach-report-preview__connectedness-block .coach-report-preview__data-table tfoot td:last-child,
        .coach-report-preview__connectedness-block .coach-report-preview__data-table th.coach-report-preview__score-heading,
        .coach-report-preview__connectedness-block .coach-report-preview__data-table td.coach-report-preview__score-col {
          width: 200px !important;
        }
        .coach-report-preview__connectedness-score-text--message {
          font-size: 11px !important;
          max-width: none !important;
          padding-right: 0 !important;
          white-space: nowrap !important;
          word-break: normal !important;
        }
        .coach-report-preview__score-bar-wrap {
          border-radius: 3px !important;
          flex: 0 0 40px !important;
          height: 5px !important;
          width: 40px !important;
        }
      }
      @page {
        size: Letter;
        /* Bottom margin reserves footer space so content flows to the next sheet. */
        margin: 0.15in 0.15in 0.32in 0.15in;

        @bottom-left {
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          content: "${footerBrandCss}";
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
          font-size: 7pt;
          padding-top: 4px;
          vertical-align: top;
        }

        @bottom-right {
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          content: "Page " counter(page) " of " counter(pages);
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
          font-size: 7pt;
          font-weight: 700;
          padding-top: 4px;
          vertical-align: top;
        }
      }
    </style>
  </head>
  <body class="coach-report-print-window">
    ${clone.outerHTML}
  </body>
</html>`);
    printWindow.document.close();
    printWindow.focus();

    // Print/PDF: score bubble LEFT of Elite Zone; white arrow from blue student dot.
    const repositionPrintChartCallouts = () => {
      try {
        const doc: any = printWindow.document;
        if (!doc) {
          return;
        }
        const PAD_LEFT = 58;
        const PAD_TOP = 54;
        const PLOT_W = 638 - 58 - 28;
        const PLOT_H = 638 - 54 - 58;
        const eliteX = PAD_LEFT + 0.7 * PLOT_W;
        const eliteY = PAD_TOP;
        const eliteW = 0.3 * PLOT_W;
        const eliteH = 0.3 * PLOT_H;
        const eliteBottom = eliteY + eliteH;
        const eliteCenterY = eliteY + eliteH * 0.5;
        // Arrow tip = Elite Zone geometric center.
        const tipX = eliteX + eliteW * 0.5;
        const tipY = eliteY + eliteH * 0.5;
        const plotRight = PAD_LEFT + PLOT_W;
        const plotBottom = PAD_TOP + PLOT_H;

        Array.prototype.forEach.call(
          doc.querySelectorAll(".wlg-chart-wrap svg"),
          (svg: any) => {
            let calloutRect = svg.querySelector("[data-wlg-callout-box='1']");
            if (!calloutRect) {
              calloutRect = svg.querySelector("g.wlg-point-callout rect");
            }
            if (!calloutRect) {
              Array.prototype.forEach.call(
                svg.querySelectorAll("rect"),
                (rect: any) => {
                  if (calloutRect) {
                    return;
                  }
                  const fill = String(
                    rect.getAttribute("fill") || ""
                  ).toLowerCase();
                  const stroke = String(
                    rect.getAttribute("stroke") || ""
                  ).toLowerCase();
                  const rw = parseFloat(rect.getAttribute("width") || "0");
                  const rh = parseFloat(rect.getAttribute("height") || "0");
                  if (
                    fill === "#ffffff" &&
                    stroke === "#2563eb" &&
                    rw > 40 &&
                    rh > 20 &&
                    rh < 80
                  ) {
                    calloutRect = rect;
                  }
                }
              );
            }

            let studentCx = 0;
            let studentCy = 0;
            let coachCx = 0;
            let coachCy = 0;
            Array.prototype.forEach.call(
              svg.querySelectorAll("circle"),
              (circle: any) => {
                const fill = String(
                  circle.getAttribute("fill") || ""
                ).toLowerCase();
                const radius = parseFloat(circle.getAttribute("r") || "0");
                const circleX = parseFloat(circle.getAttribute("cx") || "0");
                const circleY = parseFloat(circle.getAttribute("cy") || "0");
                if (
                  (fill === "#2563eb" || fill === "rgb(37, 99, 235)") &&
                  radius >= 9
                ) {
                  studentCx = circleX;
                  studentCy = circleY;
                }
                if (
                  (fill === "#e11d48" || fill === "rgb(225, 29, 72)") &&
                  radius >= 8
                ) {
                  coachCx = circleX;
                  coachCy = circleY;
                }
              }
            );

            const STUDENT_DOT_R = 16;
            const COACH_DOT_R = 13;
            const DOT_CLEAR_PAD = 14;
            const circleHitsBox = (
              cx: number,
              cy: number,
              radius: number,
              boxX: number,
              boxY: number,
              boxW: number,
              boxH: number
            ) => {
              const left = boxX - DOT_CLEAR_PAD;
              const right = boxX + boxW + DOT_CLEAR_PAD;
              const top = boxY - DOT_CLEAR_PAD;
              const bottom = boxY + boxH + DOT_CLEAR_PAD;
              const nearestX = Math.max(left, Math.min(cx, right));
              const nearestY = Math.max(top, Math.min(cy, bottom));
              const dx = cx - nearestX;
              const dy = cy - nearestY;
              return dx * dx + dy * dy < radius * radius;
            };
            const calloutHitsDots = (
              boxX: number,
              boxY: number,
              boxW: number,
              boxH: number
            ) => {
              if (
                studentCx > 0 &&
                circleHitsBox(
                  studentCx,
                  studentCy,
                  STUDENT_DOT_R,
                  boxX,
                  boxY,
                  boxW,
                  boxH
                )
              ) {
                return true;
              }
              if (
                coachCx > 0 &&
                circleHitsBox(
                  coachCx,
                  coachCy,
                  COACH_DOT_R,
                  boxX,
                  boxY,
                  boxW,
                  boxH
                )
              ) {
                return true;
              }
              return false;
            };

            // Prefer score bubble LEFT of Elite Zone, but never on blue/red dots.
            if (calloutRect) {
              const calloutW = parseFloat(
                calloutRect.getAttribute("width") || "0"
              );
              const calloutH = parseFloat(
                calloutRect.getAttribute("height") || "0"
              );
              if (calloutW > 0 && calloutH > 0) {
                const oldX = parseFloat(calloutRect.getAttribute("x") || "0");
                const oldY = parseFloat(calloutRect.getAttribute("y") || "0");
                const preferredX = Math.max(
                  PAD_LEFT + 8,
                  Math.min(eliteX - calloutW - 16, plotRight - calloutW - 8)
                );
                const yCandidates: number[] = [];
                if (studentCy > 0) {
                  yCandidates.push(studentCy - calloutH / 2);
                  yCandidates.push(studentCy - calloutH - STUDENT_DOT_R - 20);
                  yCandidates.push(studentCy + STUDENT_DOT_R + 20);
                }
                yCandidates.push(eliteCenterY - calloutH / 2);
                yCandidates.push(eliteY + 8);
                yCandidates.push(eliteBottom - calloutH - 8);
                yCandidates.push(PAD_TOP + 8);
                yCandidates.push(plotBottom - calloutH - 8);
                for (
                  let y = PAD_TOP + 8;
                  y <= plotBottom - calloutH - 8;
                  y += 12
                ) {
                  yCandidates.push(y);
                }

                const xCandidates = [
                  preferredX,
                  PAD_LEFT + 10,
                  Math.max(PAD_LEFT + 8, preferredX - 24),
                  Math.min(preferredX + 24, plotRight - calloutW - 8),
                ];

                let newX = preferredX;
                let newY = Math.max(
                  PAD_TOP + 8,
                  Math.min(
                    studentCy > 0
                      ? studentCy - calloutH / 2
                      : eliteCenterY - calloutH / 2,
                    plotBottom - calloutH - 8
                  )
                );
                let placed = false;
                for (const xCandidate of xCandidates) {
                  if (placed) {
                    break;
                  }
                  const tryX = Math.max(
                    PAD_LEFT + 8,
                    Math.min(xCandidate, plotRight - calloutW - 8)
                  );
                  for (const yCandidate of yCandidates) {
                    let tryY = Math.max(
                      PAD_TOP + 8,
                      Math.min(yCandidate, plotBottom - calloutH - 8)
                    );
                    if (tryY + calloutH > eliteBottom + 4 && tryX >= preferredX - 1) {
                      tryY = Math.min(tryY, Math.max(PAD_TOP + 8, eliteBottom - calloutH - 8));
                    }
                    if (!calloutHitsDots(tryX, tryY, calloutW, calloutH)) {
                      newX = tryX;
                      newY = tryY;
                      placed = true;
                      break;
                    }
                  }
                }

                // Last resort: keep left-of-elite X and push Y clear of both dots.
                if (!placed) {
                  newX = preferredX;
                  const clearYForDot = (
                    startY: number,
                    cx: number,
                    cy: number,
                    radius: number
                  ) => {
                    if (
                      !circleHitsBox(
                        cx,
                        cy,
                        radius,
                        newX,
                        startY,
                        calloutW,
                        calloutH
                      )
                    ) {
                      return startY;
                    }
                    const pad = DOT_CLEAR_PAD + radius;
                    const above = cy - pad - calloutH;
                    const below = cy + pad;
                    if (above >= PAD_TOP + 8) {
                      return above;
                    }
                    if (below + calloutH <= plotBottom - 8) {
                      return below;
                    }
                    return startY;
                  };
                  if (studentCx > 0) {
                    newY = clearYForDot(newY, studentCx, studentCy, STUDENT_DOT_R);
                  }
                  if (coachCx > 0) {
                    newY = clearYForDot(newY, coachCx, coachCy, COACH_DOT_R);
                  }
                  newY = Math.max(
                    PAD_TOP + 8,
                    Math.min(newY, plotBottom - calloutH - 8)
                  );
                }

                const dx = newX - oldX;
                const dy = newY - oldY;
                calloutRect.setAttribute("x", String(newX));
                calloutRect.setAttribute("y", String(newY));

                Array.prototype.forEach.call(
                  svg.querySelectorAll(
                    "[data-wlg-callout-text='1'], g.wlg-point-callout text"
                  ),
                  (textEl: any) => {
                    const textX = parseFloat(textEl.getAttribute("x") || "0");
                    const textY = parseFloat(textEl.getAttribute("y") || "0");
                    textEl.setAttribute("x", String(textX + dx));
                    textEl.setAttribute("y", String(textY + dy));
                  }
                );

                let calloutLine = svg.querySelector(
                  "[data-wlg-callout-line='1']"
                );
                if (!calloutLine) {
                  calloutLine = svg.querySelector("line.wlg-point-callout");
                }
                if (calloutLine) {
                  if (studentCx > 0 && studentCy > 0) {
                    calloutLine.setAttribute("x1", String(studentCx));
                    calloutLine.setAttribute("y1", String(studentCy));
                  }
                  calloutLine.setAttribute(
                    "x2",
                    String(
                      newX + calloutW / 2 < studentCx
                        ? newX + calloutW
                        : newX
                    )
                  );
                  calloutLine.setAttribute(
                    "y2",
                    String(
                      newY + calloutH < studentCy ? newY + calloutH : newY
                    )
                  );
                }

                // If a dot still intersects the bubble, nudge that circle group clear.
                const nudgeDotOut = (
                  fillMatchers: string[],
                  radius: number
                ) => {
                  Array.prototype.forEach.call(
                    svg.querySelectorAll("circle"),
                    (circle: any) => {
                      const fill = String(
                        circle.getAttribute("fill") || ""
                      ).toLowerCase();
                      let matched = false;
                      for (const matcher of fillMatchers) {
                        if (fill === matcher) {
                          matched = true;
                          break;
                        }
                      }
                      if (!matched) {
                        return;
                      }
                      const circleX = parseFloat(
                        circle.getAttribute("cx") || "0"
                      );
                      const circleY = parseFloat(
                        circle.getAttribute("cy") || "0"
                      );
                      if (
                        !circleHitsBox(
                          circleX,
                          circleY,
                          radius,
                          newX,
                          newY,
                          calloutW,
                          calloutH
                        )
                      ) {
                        return;
                      }
                      const left = newX - DOT_CLEAR_PAD - radius;
                      const right = newX + calloutW + DOT_CLEAR_PAD + radius;
                      const top = newY - DOT_CLEAR_PAD - radius;
                      const bottom = newY + calloutH + DOT_CLEAR_PAD + radius;
                      const distLeft = Math.abs(circleX - left);
                      const distRight = Math.abs(circleX - right);
                      const distTop = Math.abs(circleY - top);
                      const distBottom = Math.abs(circleY - bottom);
                      const minDist = Math.min(
                        distLeft,
                        distRight,
                        distTop,
                        distBottom
                      );
                      let nextX = circleX;
                      let nextY = circleY;
                      if (minDist === distLeft) {
                        nextX = left;
                      } else if (minDist === distRight) {
                        nextX = right;
                      } else if (minDist === distTop) {
                        nextY = top;
                      } else {
                        nextY = bottom;
                      }
                      nextX = Math.max(
                        PAD_LEFT + radius,
                        Math.min(plotRight - radius, nextX)
                      );
                      nextY = Math.max(
                        PAD_TOP + radius,
                        Math.min(plotBottom - radius, nextY)
                      );
                      circle.setAttribute("cx", String(nextX));
                      circle.setAttribute("cy", String(nextY));
                      if (
                        fill === "#2563eb" ||
                        fill === "rgb(37, 99, 235)"
                      ) {
                        studentCx = nextX;
                        studentCy = nextY;
                      }
                    }
                  );
                };
                if (calloutHitsDots(newX, newY, calloutW, calloutH)) {
                  nudgeDotOut(["#2563eb", "rgb(37, 99, 235)"], STUDENT_DOT_R);
                  nudgeDotOut(["#e11d48", "rgb(225, 29, 72)"], COACH_DOT_R);
                  if (calloutLine && studentCx > 0 && studentCy > 0) {
                    calloutLine.setAttribute("x1", String(studentCx));
                    calloutLine.setAttribute("y1", String(studentCy));
                  }
                }
              }
            }

            // White growth arrow from blue student dot; tip at Elite Zone center.
            // Student in elite (both scores >= 7) → hide arrow.
            const studentInElite =
              studentCx >= eliteX && studentCy <= eliteBottom;
            Array.prototype.forEach.call(
              svg.querySelectorAll("path"),
              (pathEl: any) => {
                const stroke = String(
                  pathEl.getAttribute("stroke") || ""
                ).toLowerCase();
                if (stroke !== "#ffffff" && stroke !== "white") {
                  return;
                }
                if (studentInElite || !(studentCx > 0 && studentCy > 0)) {
                  pathEl.setAttribute("display", "none");
                  return;
                }
                pathEl.removeAttribute("display");
                const rimOffset = 15;
                const toTipDx = tipX - studentCx;
                const toTipDy = tipY - studentCy;
                const toTipLen = Math.max(1, Math.hypot(toTipDx, toTipDy));
                const arrowStartX =
                  studentCx + (toTipDx / toTipLen) * rimOffset;
                const arrowStartY =
                  studentCy + (toTipDy / toTipLen) * rimOffset;
                const roughDx = tipX - arrowStartX;
                const roughDy = tipY - arrowStartY;
                const roughLen = Math.max(1, Math.hypot(roughDx, roughDy));
                const midChordX = (arrowStartX + tipX) / 2;
                const midChordY = (arrowStartY + tipY) / 2;
                const roughUx = roughDx / roughLen;
                const roughUy = roughDy / roughLen;
                const bow = Math.min(32, roughLen * 0.3);
                const ctrlX = midChordX - roughUy * bow;
                const ctrlY = midChordY + roughUx * bow;
                const headLen = 13;
                const headHalf = 7;
                const tdx = tipX - ctrlX;
                const tdy = tipY - ctrlY;
                const tlen = Math.max(1, Math.hypot(tdx, tdy));
                const ux = tdx / tlen;
                const uy = tdy / tlen;
                const baseMidX = tipX - ux * headLen;
                const baseMidY = tipY - uy * headLen;
                pathEl.setAttribute("stroke-width", "3.5");
                pathEl.setAttribute("stroke-linecap", "round");
                pathEl.setAttribute("stroke-linejoin", "round");
                pathEl.setAttribute(
                  "d",
                  "M " +
                    arrowStartX +
                    " " +
                    arrowStartY +
                    " Q " +
                    ctrlX +
                    " " +
                    ctrlY +
                    " " +
                    baseMidX +
                    " " +
                    baseMidY
                );

                // Pair polygon (arrow head) near this tip — white fill.
                Array.prototype.forEach.call(
                  svg.querySelectorAll("polygon"),
                  (poly: any) => {
                    const fill = String(
                      poly.getAttribute("fill") || ""
                    ).toLowerCase();
                    if (fill !== "#ffffff" && fill !== "white") {
                      return;
                    }
                    if (studentInElite) {
                      poly.setAttribute("display", "none");
                      return;
                    }
                    poly.removeAttribute("display");
                    const leftX = baseMidX - uy * headHalf;
                    const leftY = baseMidY + ux * headHalf;
                    const rightX = baseMidX + uy * headHalf;
                    const rightY = baseMidY - ux * headHalf;
                    poly.setAttribute(
                      "points",
                      tipX +
                        "," +
                        tipY +
                        " " +
                        leftX +
                        "," +
                        leftY +
                        " " +
                        rightX +
                        "," +
                        rightY
                    );
                  }
                );
              }
            );
          }
        );
      } catch (error) {
        console.error("Failed to reposition print chart callouts", error);
      }
    };

    const syncPrintComparisonRowHeights = () => {
      try {
        const doc: any = printWindow.document;
        if (!doc) {
          return;
        }
        syncComparisonTablesInRoot(doc.body || doc);
      } catch (error) {
        console.error("Comparison row sync failed", error);
      }
    };

    const triggerPrint = () => {
      try {
        syncPrintComparisonRowHeights();
        repositionPrintChartCallouts();
        // Keep the print/PDF window above the modal, but do not call print()
        // from the opener — that freezes reportgeneratemodal until print closes.
        try {
          printWindow.focus();
        } catch (focusError) {
          // ignore focus errors
        }
        // String timer runs in the print window realm (non-blocking for opener).
        if (printWindow && printWindow.setTimeout) {
          printWindow.setTimeout(
            "try{window.focus();window.print();}catch(e){}",
            80
          );
        }
      } catch (error) {
        console.error("Failed to open print dialog", error);
        try {
          if (printWindow && printWindow.setTimeout) {
            printWindow.setTimeout(
              "try{window.focus();window.print();}catch(e){}",
              0
            );
          }
        } catch (printError) {
          console.error("print() failed", printError);
        }
      }
    };

    try {
      printWindow.addEventListener("beforeprint", () => {
        syncPrintComparisonRowHeights();
        repositionPrintChartCallouts();
      });
    } catch (error) {
      // ignore if addEventListener unavailable
    }

    repositionPrintChartCallouts();
    browserWindow.setTimeout(repositionPrintChartCallouts, 50);
    browserWindow.setTimeout(repositionPrintChartCallouts, 150);
    if (!(options && options.fast)) {
      browserWindow.setTimeout(repositionPrintChartCallouts, 350);
      browserWindow.setTimeout(repositionPrintChartCallouts, 650);
    }

    const printDelay = options && options.fast ? 0 : 700;
    // Schedule from the print window so the opener/modal JS thread is not held.
    const scheduleTrigger =
      printWindow && printWindow.setTimeout
        ? (fn: any, ms: number) => printWindow.setTimeout(fn, ms)
        : (fn: any, ms: number) => browserWindow.setTimeout(fn, ms);
    if (printWindow.document.readyState === "complete") {
      scheduleTrigger(triggerPrint, printDelay);
    } else {
      printWindow.onload = () => {
        scheduleTrigger(triggerPrint, printDelay);
      };
    }
  };

  React.useEffect(() => {
    if (!autoPrint || isLoadingPreview || isPrintMode || didAutoPrintRef.current) {
      return;
    }
    didAutoPrintRef.current = true;
    const browserWindow: any = Function("return this")();
    let attempts = 0;
    const tryAutoPrint = () => {
      attempts += 1;
      if (!printRootRef.current && attempts < 20) {
        if (browserWindow && browserWindow.setTimeout) {
          browserWindow.setTimeout(tryAutoPrint, 30);
        }
        return;
      }
      openPrintPreviewWindow(autoPrintWindow, { fast: true });
      if (onAutoPrintHandled) {
        onAutoPrintHandled();
      }
    };
    const timeoutId =
      browserWindow && browserWindow.setTimeout
        ? browserWindow.setTimeout(tryAutoPrint, 0)
        : 0;
    return () => {
      if (timeoutId && browserWindow && browserWindow.clearTimeout) {
        browserWindow.clearTimeout(timeoutId);
      }
    };
  }, [autoPrint, isLoadingPreview, isPrintMode, autoPrintWindow]);

  React.useEffect(() => {
    if (!autoPrint) {
      didAutoPrintRef.current = false;
    }
  }, [autoPrint]);

  React.useEffect(() => {
    if (!bindOpenPrint) {
      return;
    }
    if (isPrintMode) {
      bindOpenPrint(null);
      return;
    }
    // Keep print bound immediately (no loading gate) so PRINT DATA ONLY opens PDF instantly.
    bindOpenPrint((targetWindow?: any) => {
      openPrintPreviewWindow(targetWindow, { fast: true });
    });
    return () => {
      bindOpenPrint(null);
    };
  }, [bindOpenPrint, isPrintMode, report, hasUnsavedChanges]);

  const renderEditableTextarea = (
    value: string,
    maxLength: number,
    className: string,
    placeholder: string,
    onChange?: (nextValue: string) => void
  ) => {
    const limitedValue =
      typeof value === "string" && value.length > maxLength
        ? value.slice(0, maxLength)
        : value || "";
    const isReadOnlyField = !onChange;
    const isHighlight =
      className.indexOf("coach-report-preview__editable-text--highlight") !== -1;
    const isGrowable =
      isHighlight ||
      className.indexOf("coach-report-preview__editable-text--suggestion") !== -1 ||
      className.indexOf("coach-report-preview__editable-text--summary") !== -1 ||
      className.indexOf("coach-report-preview__editable-text--resource-title") !==
        -1 ||
      className.indexOf("coach-report-preview__editable-text--resource-body") !==
        -1 ||
      className.indexOf("coach-report-preview__editable-text--resource-why-chosen") !==
        -1 ||
      className.indexOf("coach-report-preview__editable-text--parent-summary") !==
        -1 ||
      className.indexOf("coach-report-preview__editable-text--final-note") !== -1 ||
      className.indexOf("coach-report-preview__editable-text--commitments") !== -1 ||
      className.indexOf("coach-report-preview__admin-comments-body") !== -1 ||
      className.indexOf("coach-report-preview__admin-comments-signature") !== -1;
    return (
      <div className="coach-report-preview__editable-field-wrap">
        <div className="coach-report-preview__editable-field-surface">
          <div
            aria-hidden="true"
            className={`${className} coach-report-preview__print-text${
              limitedValue ? "" : " coach-report-preview__print-text--placeholder"
            }`}
            data-maxlength={maxLength}
            onClick={
              !isReadOnlyField
                ? (event: any) => {
                    const surface: any =
                      event && event.currentTarget
                        ? event.currentTarget.parentNode
                        : null;
                    const textarea: any = surface
                      ? surface.querySelector(
                          "textarea.coach-report-preview__edit-field"
                        )
                      : null;
                    if (textarea && textarea.focus) {
                      textarea.focus();
                    }
                  }
                : undefined
            }
          >
            {limitedValue
              ? renderTextWithBoldScores(limitedValue)
              : placeholder}
          </div>
          <textarea
            className={`${className} coach-report-preview__edit-field`}
            data-maxlength={maxLength}
            maxLength={maxLength}
            placeholder={placeholder}
            readOnly={isReadOnlyField}
            tabIndex={isReadOnlyField ? -1 : 0}
            rows={isGrowable ? 1 : undefined}
            value={limitedValue}
            style={{
              /* Highlights: omit height so syncHighlightHeights sizes to content. */
              height: isGrowable && !isHighlight ? "auto" : undefined,
              overflowY: isGrowable && !isHighlight ? "hidden" : undefined,
              pointerEvents: isReadOnlyField ? "none" : undefined,
            }}
            onFocus={
              !isReadOnlyField
                ? (event: any) => {
                    if (isHighlight) {
                      if (event && event.target) {
                        resizeHighlightTextarea(event.target);
                      }
                      return;
                    }
                    if (event && event.target) {
                      autoResizeTextarea(event.target);
                    }
                  }
                : undefined
            }
            onChange={
              onChange
                ? (event: any) => {
                    const nextValue = String(
                      (event && event.target && event.target.value) || ""
                    );
                    onChange(
                      nextValue.length > maxLength
                        ? nextValue.slice(0, maxLength)
                        : nextValue
                    );
                    if (isGrowable) {
                      if (isHighlight) {
                        if (event && event.target) {
                          resizeHighlightTextarea(event.target);
                        }
                      } else if (event && event.target) {
                        autoResizeTextarea(event.target);
                      }
                    }
                  }
                : undefined
            }
          />
        </div>
        {!isReadOnlyField && (
          <div className="coach-report-preview__char-limit">
            {limitedValue.length}/{maxLength}
          </div>
        )}
      </div>
    );
  };

  // Modal preview shows 4 AI pages only (raw/print pages are screen-hidden).
  // Print restamps Page X of Y from actual .coach-report-preview__page nodes.
  const reportPageTotal = pageName === "reportmodal" ? 5 : 8;
  const renderPageFooter = (pageNumber: number) => (
    <div className="coach-report-preview__page-footer">
      <div className="coach-report-preview__footer-brand">{footerBrandDisplay}</div>
      <div className="coach-report-preview__footer-page">
        Page {pageNumber} of {reportPageTotal}
      </div>
    </div>
  );

  const renderRunningHeader = () => (
    <div className="coach-report-preview__running-header">
      <div className="coach-report-preview__running-header-left">
        {report.schoolName} · {terminology.reportSubject} {report.coachName} · {report.sportName}
      </div>
      {/* <div className="coach-report-preview__running-header-right">{seasonLabelText}</div> */}
    </div>
  );

  const renderScoreMeter = (score: string, role: IScoreMeterRole) => {
    const tone = getScoreMeterTone(role, score);
    return (
      <div
        className={`coach-report-preview__score-meter coach-report-preview__score-meter--${tone}`}
        title={`${formatScoreDisplay(score)} / 10`}
      >
        <span className="coach-report-preview__score-meter-value">
          {formatScoreDisplay(score)}
        </span>
      </div>
    );
  };

  /** Parent / Team Connectedness style: "9.6/10" + teal fill bar (dynamic). */
  const renderTableScoreBar = (score: string, options?: { showBar?: boolean }) => {
    const numeric = isNumericScore(score);
    const showBar = options?.showBar !== false && numeric;
    const percent = parseScorePercent(score);
    return (
      <div
        className={`coach-report-preview__score-cell${
          numeric ? "" : " coach-report-preview__score-cell--message"
        }`}
      >
        <span
          className={`coach-report-preview__connectedness-score-text${
            numeric ? "" : " coach-report-preview__connectedness-score-text--message"
          }`}
        >
          {formatScoreOutOfTen(score)}
        </span>
        {showBar ? (
          <div className="coach-report-preview__score-bar-wrap">
            <div
              className="coach-report-preview__score-bar"
              style={{ width: `${percent}%` }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderComparisonSideCells = (
    row: IReportComparisonRow | undefined,
    side: string
  ) => {
    if (!row) {
      return [
        <td key={`${side}-label`} data-comparison-spacer="1">
          &nbsp;
        </td>,
        <td
          key={`${side}-student`}
          className="coach-report-preview__score-col"
          data-comparison-spacer="1"
        >
          &nbsp;
        </td>,
        <td
          key={`${side}-coach`}
          className="coach-report-preview__score-col"
          data-comparison-spacer="1"
        >
          &nbsp;
        </td>,
      ];
    }
    return [
      <td key={`${side}-label`}>{row.label}</td>,
      <td key={`${side}-student`} className="coach-report-preview__score-col">
        {renderScoreMeter(row.studentScore, "student")}
      </td>,
      <td key={`${side}-coach`} className="coach-report-preview__score-col">
        {renderScoreMeter(row.coachScore, "coach")}
      </td>,
    ];
  };

  /**
   * One HTML table for Expectations + Relational Warmth so matching <tr>
   * heights keep Average footers aligned (print + screen).
   */
  const renderPairedComparisonSections = (
    left: IComparisonSection,
    right: IComparisonSection
  ) => {
    const leftRows = (left && left.rows) || [];
    const rightRows = (right && right.rows) || [];
    const rowIndexes = rangeIndexes(
      Math.max(leftRows.length, rightRows.length)
    );
    return (
      <div className="coach-report-preview__comparison-pair">
        <div className="coach-report-preview__comparison-pair-headers">
          <div>{renderSectionHeader(left.title)}</div>
          <div>{renderSectionHeader(right.title)}</div>
        </div>
        <div className="coach-report-preview__comparison-table-wrap coach-report-preview__comparison-table-wrap--pair">
          <table className="coach-report-preview__comparison-table coach-report-preview__comparison-table--pair">
            <colgroup>
              <col className="coach-report-preview__comparison-pair-col-item" />
              <col className="coach-report-preview__comparison-pair-col-score" />
              <col className="coach-report-preview__comparison-pair-col-score" />
              <col className="coach-report-preview__comparison-pair-col-gap" />
              <col className="coach-report-preview__comparison-pair-col-item" />
              <col className="coach-report-preview__comparison-pair-col-score" />
              <col className="coach-report-preview__comparison-pair-col-score" />
            </colgroup>
            <thead>
              <tr>
                <th>Survey Item</th>
                <th className="coach-report-preview__score-col">{terminology.respondentShort} Avg</th>
                <th className="coach-report-preview__score-col">{terminology.selfScore}</th>
                <th
                  aria-hidden="true"
                  className="coach-report-preview__comparison-pair-gap"
                />
                <th>Survey Item</th>
                <th className="coach-report-preview__score-col">{terminology.respondentShort} Avg</th>
                <th className="coach-report-preview__score-col">{terminology.selfScore}</th>
              </tr>
            </thead>
            <tbody>
              {rowIndexes.map((index) => (
                <tr key={`comparison-pair-${index}`}>
                  {renderComparisonSideCells(leftRows[index], "left")}
                  <td
                    aria-hidden="true"
                    className="coach-report-preview__comparison-pair-gap"
                  />
                  {renderComparisonSideCells(rightRows[index], "right")}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <strong>{left.averageLabel}</strong>
                </td>
                <td className="coach-report-preview__score-col">
                  <strong>{formatScoreDisplay(left.studentAverage)}</strong>
                </td>
                <td className="coach-report-preview__score-col">
                  <strong>{formatScoreDisplay(left.coachAverage)}</strong>
                </td>
                <td
                  aria-hidden="true"
                  className="coach-report-preview__comparison-pair-gap"
                />
                <td>
                  <strong>{right.averageLabel}</strong>
                </td>
                <td className="coach-report-preview__score-col">
                  <strong>{formatScoreDisplay(right.studentAverage)}</strong>
                </td>
                <td className="coach-report-preview__score-col">
                  <strong>{formatScoreDisplay(right.coachAverage)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="coach-report-preview__comparison-pair-summaries">
          <div className="coach-report-preview__summary-box">
            <div className="coach-report-preview__summary-box-label">
              {left.title.toUpperCase()} SUMMARY
            </div>
            <div className="coach-report-preview__summary-box-text">
              {renderTextWithBoldScores(left.summary)}
            </div>
          </div>
          <div className="coach-report-preview__summary-box">
            <div className="coach-report-preview__summary-box-label">
              {right.title.toUpperCase()} SUMMARY
            </div>
            <div className="coach-report-preview__summary-box-text">
              {renderTextWithBoldScores(right.summary)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const accentColor =
    primaryColor && primaryColor.trim() ? primaryColor.trim() : "#79bde8";
  const accentRgb = hexToRGB(accentColor) || "121, 189, 232";
  const chartLabel =
    (coachName && coachName.trim()) || report.coachName || "Coach";
  const leaderCommitmentsIntroText = `Based on this evaluation, ${report.coachName || "the coach"} has identified the following personal commitments and goals for the upcoming season. These will be reviewed at the start of next year's evaluation cycle.`;
  const seasonLabelText = formatSeasonLabel(report.seasonLabel);
  const reportSubtitleText = formatSubtitleLabel(report.reportSubtitle).replace(
    /Coaching Feedback Report/g,
    "Teaching Feedback Report"
  );
  const footerBrandDisplay =
    report.footerBrand && report.seasonLabel
      ? String(report.footerBrand)
          .replace(report.seasonLabel, "")
          .replace(seasonLabelText, "")
          .replace(/Coaching Feedback Report/g, "Teaching Feedback Report")
          .trim()
      : formatSeasonLabel(report.footerBrand).replace(
          /Coaching Feedback Report/g,
          "Teaching Feedback Report"
        );

  if (isLoadingPreview && !readOnly) {
    return (
      <div
        className="coach-report-preview"
        style={{
          position: "relative",
        }}
      >
        <style>{reportDesignCss}</style>
        <style>
          {`
            @keyframes coach-report-preview-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        <div
          style={{
            alignItems: "center",
            backdropFilter: "blur(3px)",
            background: `rgba(${accentRgb}, 0.35)`,
            display: "flex",
            inset: 0,
            justifyContent: "center",
            minHeight: isPrintMode ? "400px" : "70vh",
            position: "absolute",
            zIndex: 2,
          }}
        >
          <div
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              justifyContent: "center",
            }}
          >
            <div
              style={{
                animation: "coach-report-preview-spin 0.9s linear infinite",
                border: "4px solid rgba(255, 255, 255, 0.35)",
                borderRadius: "50%",
                borderTopColor: "#ffffff",
                height: 36,
                width: 36,
              }}
            />
            <div
              style={{
                color: "#ffffff",
                fontSize: 18,
                fontWeight: 700,
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.35)",
              }}
            >
              Loading report preview...
            </div>
          </div>
        </div>
        <div
          className="coach-report-preview__page"
          style={{
            minHeight: isPrintMode ? "400px" : "70vh",
            opacity: 0.35,
          }}
        >
          <div className="coach-report-preview__page-content" />
        </div>
      </div>
    );
  }

  if (hasPreviewData === false && !autoPrint) {
    return (
      <div
        className="coach-report-preview"
        style={{
          alignItems: "center",
          color: "#333",
          display: "flex",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          justifyContent: "center",
          minHeight: "100%",
          padding: "32px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 680, marginTop: 8 }}>
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 10, fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
            No report data available for this subject.
          </div>
          <div style={{ color: "#666", fontSize: 16, lineHeight: 1.6 }}>
            There is no report data available for the selected coach, season, and subject.
          </div>
        </div>
      </div>
    );
  }

  const warmthFromSurvey =
    survey && survey.scaleResponseSummaries
      ? survey.scaleResponseSummaries.warmth.studentTotalAverage
      : undefined;
  const expectationsFromSurvey =
    survey && survey.scaleResponseSummaries
      ? survey.scaleResponseSummaries.expectations.studentTotalAverage
      : undefined;
  const coachWarmthFromSurvey =
    survey && survey.scaleResponseSummaries
      ? survey.scaleResponseSummaries.warmth.coachTotalAverage
      : undefined;
  const coachExpectationsFromSurvey =
    survey && survey.scaleResponseSummaries
      ? survey.scaleResponseSummaries.expectations.coachTotalAverage
      : undefined;

  const warmthScore =
    warmthFromSurvey !== undefined
      ? warmthFromSurvey
      : parseScoreValue(
          report.kpis.find((item) => item.key === "relationalWarmthScore")?.value ||
            report.relationalWarmth.studentAverage
        );
  const expectationsScore =
    expectationsFromSurvey !== undefined
      ? expectationsFromSurvey
      : parseScoreValue(
          report.kpis.find((item) => item.key === "expectationsScore")?.value ||
            report.expectations.studentAverage
        );
  const coachWarmthScore =
    coachWarmthFromSurvey !== undefined
      ? coachWarmthFromSurvey
      : parseScoreValue(report.relationalWarmth.coachAverage);
  const coachExpectationsScore =
    coachExpectationsFromSurvey !== undefined
      ? coachExpectationsFromSurvey
      : parseScoreValue(report.expectations.coachAverage);

  const quadrant = getQuadrant(warmthScore, expectationsScore);
  const warmthDisplay = formatScoreOutOfTen(warmthScore);
  const expectationsDisplay = formatScoreOutOfTen(expectationsScore);
  const quadrantTitle =
    report.wiseLeadership.quadrant || quadrant.title;
  const quadrantSummary =
    report.wiseLeadership.summary || quadrant.description(chartLabel);
  const placementTint = `rgba(${accentRgb}, 0.15)`;

const hasCommentItems = (section?: { comments?: any[] }) => {
  const comments = section?.comments;
  return Array.isArray(comments) && comments.length > 0;
};

  const hasCoachResponse = (section?: { placeholder?: string }) => {
    const value = section?.placeholder?.trim() || "";
    return (
      value.length > 0 &&
      value !== "Coach comments will appear here when submitted through the platform." &&
      value !== "AD comments will appear here when submitted through the platform."
    );
  };

  const splitIntoColumns = <T,>(items: T[]) => {
    const midpoint = Math.ceil(items.length / 2);
    return [items.slice(0, midpoint), items.slice(midpoint)] as const;
  };

  const renderCommentItem = (
    item: { key: string; title: string; body: string },
    index: number
  ) => {
    const { label, bodyText } = parseCommentContent(item.title, item.body);
    return (
      <div className="coach-report-preview__comment-item" key={item.key}>
        <div className="coach-report-preview__comment-num">{index + 1}</div>
        <div className="coach-report-preview__comment-text">
          {label ? (
            <span className="coach-report-preview__comment-title">{label}: </span>
          ) : null}
          <span className="coach-report-preview__comment-body">
            {renderTextWithBoldScores(bodyText)}
          </span>
        </div>
      </div>
    );
  };

  /**
   * Side-by-side comment columns as independent stacks (matches reportt.html).
   * Avoids paired table rows so short coach boxes are not stretched by taller student content.
   */
  const renderCommentPairTables = (
    pairs: Array<{
      key: string;
      leftQuestion?: string;
      rightQuestion?: string;
      rightQuestionClassName?: string;
      leftBody: any;
      rightBody: any;
    }>,
    headers?: any
  ) => {
    const renderColumnSection = (
      key: string,
      question: string | undefined,
      questionClassName: string | undefined,
      body: any
    ) => {
      if (!question && !body) {
        return null;
      }
      return (
        <div className="coach-report-preview__comment-section" key={key}>
          {question ? (
            <div
              className={
                questionClassName || "coach-report-preview__comment-question"
              }
            >
              {question}
            </div>
          ) : null}
          {body}
        </div>
      );
    };

    if (!pairs.length) {
      return headers ? (
        <div className="coach-report-preview__comment-summaries-block">
          {headers}
        </div>
      ) : null;
    }

    return (
      <div className="coach-report-preview__comment-summaries-block">
        <div className="coach-report-preview__comment-summaries-keep">
          {headers}
        </div>
        <div className="coach-report-preview__comment-summaries-columns">
          <div className="coach-report-preview__comment-summaries-col">
            {pairs.map((pair) =>
              renderColumnSection(
                pair.key + "-left",
                pair.leftQuestion,
                undefined,
                pair.leftBody
              )
            )}
          </div>
          <div className="coach-report-preview__comment-summaries-col">
            {pairs.map((pair) =>
              renderColumnSection(
                pair.key + "-right",
                pair.rightQuestion,
                pair.rightQuestionClassName,
                pair.rightBody
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const studentSections = report.studentCommentSummaries.filter(hasCommentItems);
  const coachSections = report.coachCommentSummaries.filter(hasCoachResponse);
  const parentSections = report.parentFeedback.commentSections.filter(hasCommentItems);
  const [leftParentSections, rightParentSections] = splitIntoColumns(parentSections);
  const showAgendaSections =
    displayMode === "full" || displayMode === "agenda";
  const showPrintDataSections =
    displayMode === "full" || displayMode === "printData";
  const showAiSections =
    pageName !== "evalprintform" && showAgendaSections;
  const showRawSections = showPrintDataSections;
  const showPrintOnlyFromModal = pageName === "reportmodal";

  return (
    <div
      className="coach-report-preview"
      ref={printRootRef}
      style={
        {
          "--coach-report-accent": accentColor,
          "--coach-report-accent-rgb": accentRgb,
        } as any
      }
    >
      {!isPrintMode && (
        <div className="coach-report-preview__toolbar">
          <div className="coach-report-preview__save-status">
            {readOnly ? "" : saveStatus}
          </div>
          <div className="coach-report-preview__toolbar-actions">
            {!readOnly && (
              <button
                className="coach-report-preview__save-button"
                disabled={isSaving || !report.reportId || !onSaveReport}
                onClick={saveReport}
                title={
                   report.reportId
                    ? "Save report changes"
                    : "Report id not loaded yet"
                }
                type="button"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            )}
            <button
              className="coach-report-preview__print-button"
              disabled={hasUnsavedChanges}
              onClick={() => openPrintPreviewWindow()}
              title={
                hasUnsavedChanges
                  ? "Save report changes before printing"
                  : "Print report"
              }
              type="button"
            >
              Print
            </button>
          </div>
        </div>
      )}

      <style>{reportDesignCss}</style>
      <style>{`
        .coach-report-preview,
        .coach-report-preview * {
          font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
        }

        .coach-report-preview__print-only-from-modal {
          display: none;
        }

        /* Modal: center Score heading + values (matches reportt.html). */
        .coach-report-preview__data-table th.coach-report-preview__score-heading,
        .coach-report-preview__data-table td.coach-report-preview__score-col {
          text-align: center !important;
          white-space: nowrap !important;
          width: 100px !important;
        }
        .coach-report-preview__connectedness-block .coach-report-preview__data-table th.coach-report-preview__score-heading,
        .coach-report-preview__connectedness-block .coach-report-preview__data-table td.coach-report-preview__score-col {
          width: 200px !important;
        }
        .coach-report-preview__score-cell {
          gap: 5px !important;
        }
        .coach-report-preview__score-bar-wrap {
          border-radius: 3px !important;
          flex: 0 0 40px !important;
          height: 5px !important;
          width: 40px !important;
        }
        .coach-report-preview__data-table thead th:first-child,
        .coach-report-preview__data-table tbody td:first-child,
        .coach-report-preview__data-table tfoot td:first-child {
          text-align: left !important;
        }
        .coach-report-preview__score-cell,
        .coach-report-preview__score-cell--message {
          align-items: center !important;
          display: flex !important;
          justify-content: center !important;
          width: 100% !important;
        }
        .coach-report-preview__connectedness-score-text--message {
          max-width: none !important;
          padding-right: 0 !important;
          text-align: center !important;
          white-space: nowrap !important;
          word-break: normal !important;
        }

        /* Comparison tables: center Stud Avg / Coach scores under headers */
        .coach-report-preview__comparison-table td.coach-report-preview__score-col,
        .coach-report-preview__comparison-table tfoot td.coach-report-preview__score-col,
        .coach-report-preview__comparison-table thead th.coach-report-preview__score-col {
          text-align: center !important;
        }
        .coach-report-preview__comparison-table .coach-report-preview__score-meter {
          display: flex !important;
          justify-content: center !important;
          width: 100% !important;
        }
        .coach-report-preview__comparison-table .coach-report-preview__score-meter-value {
          text-align: center !important;
        }

        .coach-report-preview__editable-text--resource-title,
        .coach-report-preview__editable-text--resource-body,
        .coach-report-preview__editable-text--resource-why-chosen {
          height: auto;
          max-height: none;
          min-height: 0;
          overflow: visible;
        }

        .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field,
        .coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field,
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          margin: 0;
          outline: none;
          padding: 0;
          resize: none;
        }

        .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field:focus,
        .coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field:focus,
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field:focus {
          /* Match reportt.html [contenteditable]:focus */
          background: rgba(100, 160, 255, 0.1);
          border-color: transparent;
          border-radius: 4px;
          box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
          outline: none;
        }

        .coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field,
        .coach-report-preview__editable-text--resource-title.coach-report-preview__print-text,
        .coach-report-preview__resource-title-big {
          color: #1a2a3a;
          display: block;
          font-size: 9pt;
          font-weight: 700;
          line-height: 1.35;
          margin: 0 0 0px;
          min-height: 0;
        }

        .coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field {
          color: #334155;
          display: block;
          font-size: 7.5pt;
          line-height: 1.55;
          margin: 0;
        }
        .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
          color: #64748b;
          display: inline;
          font-size: 7.5pt;
          font-style: italic;
          font-weight: 400;
          line-height: 1.55;
          margin: 0;
          width: auto;
        }

        .coach-report-preview__resource-desc {
          color: #334155;
          display: block;
          font-size: 7.5pt;
          line-height: 1.55;
          margin: 0;
        }

        .coach-report-preview__resource-why-chosen {
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          font-size: 7.5pt;
          font-style: italic;
          line-height: 1.55;
          margin-top: 0px;
          padding-top: 5px;
        }
        .coach-report-preview__resource-why-chosen-para {
          margin: 0;
          padding: 0;
        }
        .coach-report-preview__resource-why-chosen-label,
        .coach-report-preview__resource-why-chosen strong {
          color: #1a2a3a;
          display: inline;
          float: none;
          font-style: italic;
          font-weight: 700;
          margin: 0;
        }
        .coach-report-preview__resource-why-chosen-text {
          color: #64748b;
          display: inline;
          font-style: italic;
          font-weight: 400;
        }

        .coach-report-preview__resource-why-chosen .coach-report-preview__editable-field-wrap {
          display: inline;
          overflow: visible;
          width: auto;
        }

        .coach-report-preview__resource-type-badge {
          border-radius: 20px;
          display: inline-block;
          font-size: 6.5pt;
          font-weight: 700;
          letter-spacing: 0.8px;
          line-height: 1.2;
          margin-bottom: 6px;
          padding: 2px 8px;
          text-transform: uppercase;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .coach-report-preview__resource-card-big--book .coach-report-preview__resource-type-badge {
          background: #dce8f5;
          color: #1a3a5c;
        }
        .coach-report-preview__resource-card-big--article .coach-report-preview__resource-type-badge {
          background: #d0f0ec;
          color: #0d7a6e;
        }
        .coach-report-preview__resource-card-big--podcast .coach-report-preview__resource-type-badge {
          background: #fef3c7;
          color: #b45309;
        }
        .coach-report-preview__resource-card-big {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-left-width: 4px;
          border-left-style: solid;
          border-radius: 10px;
          box-shadow: none;
          overflow: hidden;
          padding: 14px 16px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .coach-report-preview__resource-card-big--book {
          border-left-color: #1a3a5c;
        }
        .coach-report-preview__resource-card-big--article {
          border-left-color: #0d7a6e;
        }
        .coach-report-preview__resource-card-big--podcast {
          border-left-color: #b45309;
        }
        .coach-report-preview__resource-list {
          gap: 10px;
        }
        .coach-report-preview__resource-title-big {
          color: #1a2a3a;
          font-size: 9pt;
          font-weight: 700;
          line-height: 1.3;
          margin: 0 0 5px;
        }
        .coach-report-preview__resource-desc {
          color: #334155;
          font-size: 7.5pt;
          line-height: 1.5;
          margin: 0 0 5px;
        }

        .coach-report-preview__resource-card-top {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          width: 100%;
        }

        .coach-report-preview__resource-card-actions {
          display: flex;
          justify-content: flex-end;
          margin: 0;
        }

        .coach-report-preview__resource-card-top
          > .coach-report-preview__resource-type-badge:only-child {
          margin-right: auto;
        }

        .coach-report-preview__resource-remove {
          background: transparent;
          border: none;
          color: #8a97ab;
          cursor: pointer;
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          height: auto;
          line-height: 1.2;
          padding: 0;
          text-transform: uppercase;
        }

        .coach-report-preview__resource-remove:hover {
          color: #c23a3a;
        }

        .coach-report-preview__resource-undo-row {
          display: flex;
          justify-content: flex-end;
          margin: -4px 0 8px;
        }

        .coach-report-preview__resource-undo {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          color: #1f334d;
          cursor: pointer;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.2;
          padding: 6px 10px;
        }

        .coach-report-preview__resource-undo:hover {
          border-color: #8a97ab;
        }

        .coach-report-preview__resource-remove-icon {
          display: none;
        }

        .coach-report-preview__resource-remove-label {
          display: inline;
        }

        .coach-report-preview__editable-text--resource-title + .coach-report-preview__char-limit,
        .coach-report-preview__editable-text--resource-body + .coach-report-preview__char-limit {
          align-self: flex-end;
          display: block;
          float: none;
          margin-left: auto;
          margin-right: 0;
          margin-top: 4px;
          margin-bottom: 8px;
          text-align: right;
          width: auto;
        }
        .coach-report-preview__resource-why-chosen .coach-report-preview__char-limit {
          clear: none;
          display: block;
          float: none;
          margin-left: auto;
          margin-right: 0;
          margin-top: 6px;
          margin-bottom: 0;
          text-align: right;
          width: fit-content;
        }

        @media print {
          .coach-report-preview__print-only-from-modal {
            display: block !important;
          }
          .coach-report-preview__screen-only-from-modal {
            display: none !important;
          }
        }
      `}</style>
      {showAiSections && (
      <div className="coach-report-preview__page">
        <div className="coach-report-preview__report-header">
          <div className="coach-report-preview__header-top">
            <div>
              <div className="coach-report-preview__school-name">{report.schoolName}</div>
              <div className="coach-report-preview__report-subtitle">{reportSubtitleText}</div>
            </div>
            <div className="coach-report-preview__school-logo">
              {report.schoolLogoUrl ? (
                <img
                  className="coach-report-preview__school-logo-image"
                  src={report.schoolLogoUrl}
                  alt={`${report.schoolName} logo`}
                />
              ) : (
                report.schoolLogoText
              )}
            </div>
          </div>
          <div className="coach-report-preview__header-meta">
            <div className="coach-report-preview__meta-item">
              <div className="coach-report-preview__meta-label">Date</div>
<div className="coach-report-preview__meta-value">{new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-")}</div>
            </div>
            <div className="coach-report-preview__meta-item">
              <div className="coach-report-preview__meta-label">{terminology.reportSubject}</div>
              <div className="coach-report-preview__meta-value">{report.coachName}</div>
            </div>
            <div className="coach-report-preview__meta-item">
              <div className="coach-report-preview__meta-label">Subject</div>
              <div className="coach-report-preview__meta-value">{report.sportName}</div>
            </div>
          </div>
        </div>

        <div className="coach-report-preview__page-content">
          <div className={showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}>
            <div className="coach-report-preview__kpi-grid">
              {report.kpis.map((item) => {
                const rawKpiValue =
                  item.key === "teamConnectednessScore"
                    ? report.teamConnectedness?.overallScore
                    : item.value;
                const kpiValue = rawKpiValue == null ? "" : formatScoreDisplay(String(rawKpiValue));
                const isLongKpi =
                  kpiValue.length > 10 || /[a-zA-Z]/.test(kpiValue);
                return (
                <div className="coach-report-preview__kpi-card" key={item.key}>
                  <div
                    className={`coach-report-preview__kpi-value${
                      isLongKpi ? " coach-report-preview__kpi-value--long" : ""
                    }`}
                  >
                    {kpiValue || "—"}
                  </div>
                  <div className="coach-report-preview__kpi-label">{item.label}</div>
                </div>
                );
              })}
            </div>
          </div>

          {renderSectionHeader("Overall Summary")}
          <div className="coach-report-preview__overall-summary-box">
          {renderEditableTextarea(
            report.overallSummary,
            report.overallSummaryMaxLength,
            "coach-report-preview__editable-text coach-report-preview__editable-text--summary",
            "Enter overall summary here...",
            isEditable
              ? (nextValue) => updateReport((current) => ({ ...current, overallSummary: nextValue }))
              : undefined
          )}
    </div>
          {renderSectionHeader("Highlights & Strengths")}
          <div className="coach-report-preview__highlight-grid">
            {report.highlightsStrengths.map((item) => (
              <div className="coach-report-preview__highlight-card" key={item.key}>
                <div className="coach-report-preview__highlight-num">
                  {formatBadgeLabel(item.badge)}
                </div>
                <div className="coach-report-preview__highlight-title">{item.title}</div>
                {renderEditableTextarea(
                  item.body,
                  COACH_REPORT_LIMITS.highlightBody,
                  "coach-report-preview__editable-text coach-report-preview__editable-text--highlight",
                  "Enter highlight here...",
                  isEditable
                    ? (nextValue) =>
                        updateReport((current) => ({
                          ...current,
                          highlightsStrengths: current.highlightsStrengths.map((highlight) =>
                            highlight.key === item.key
                              ? {
                                  ...highlight,
                                  body: nextValue,
                                  maxLength: COACH_REPORT_LIMITS.highlightBody,
                                }
                              : highlight
                          ),
                        }))
                    : undefined
                )}
              </div>
            ))}
          </div>

          <div className="coach-report-preview__connectedness-block">
            {renderSectionHeader("Classroom Connectedness")}
            {/* {report.teamConnectedness.intro ? (
            <p className="coach-report-preview__intro-text">
              {renderTextWithBoldScores(report.teamConnectedness.intro)}
            </p>
          ) : null} */}
            <table className="coach-report-preview__data-table">
              <thead>
                <tr>
                  <th>Connectedness Marker</th>
                  <th className="coach-report-preview__score-heading">Score</th>
                </tr>
              </thead>
              <tbody>
                {report.teamConnectedness.rows.map((item) => (
                  <tr key={item.key}>
                    <td>{item.label}</td>
                    <td className="coach-report-preview__score-col">
                      {renderTableScoreBar(item.score)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Overall Classroom Connectedness Score</strong>
                  </td>
                  <td className="coach-report-preview__score-col">
                    {renderTableScoreBar(report.teamConnectedness.overallScore, {
                      showBar: false,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
             {/* <p className="coach-report-preview__connectedness-footer-note">
              If you'd like to receive reports on Classroom Connectedness, please reach out to Grit Leadership to update your template.
            </p> */}
          </div>
        </div>
        {renderPageFooter(1)}
      </div>
      )}

      {showAiSections && (
      <div
        className={`coach-report-preview__page${
          showPrintOnlyFromModal
            ? " coach-report-preview__screen-only-from-modal"
            : ""
        }`}
      >
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderSectionHeader("Parent Feedback")}
          <div className="coach-report-preview__summary-box">
            <div className="coach-report-preview__summary-box-label">Parent Feedback Summary</div>
            {renderEditableTextarea(
              report.parentFeedback.summary,
              COACH_REPORT_LIMITS.parentSummary,
              "coach-report-preview__editable-text coach-report-preview__editable-text--parent-summary",
              "Enter parent feedback summary here...",
              isEditable
                ? (nextValue) =>
                    updateReport((current) => ({
                      ...current,
                      parentFeedback: {
                        ...current.parentFeedback,
                        summary: nextValue,
                      },
                    }))
                : undefined
            )}
          </div>
        </div>
        {renderPageFooter(2)}
      </div>
      )}

      {showAiSections && (
      <div className="coach-report-preview__page">
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderSectionHeader("Suggestions for Continued Development")}
          <div className="coach-report-preview__suggestion-list">
            {report.suggestions.map((item) => (
              <div className="coach-report-preview__suggestion-item" key={item.key}>
                <div className="coach-report-preview__suggestion-icon">{item.number}</div>
                <div className="coach-report-preview__suggestion-content">
                  {isEditable ? (
                    <div className="coach-report-preview__resource-card-actions">
                      <button
                        type="button"
                        className="coach-report-preview__resource-remove"
                        onClick={() => removeResource(item.key, "suggestions")}
                      >
                        <span className="coach-report-preview__resource-remove-label">Remove</span>
                      </button>
                    </div>
                  ) : null}
                  <div className="coach-report-preview__suggestion-title">{item.title}</div>
                  {renderEditableTextarea(
                    item.body,
                    item.maxLength,
                    "coach-report-preview__editable-text coach-report-preview__editable-text--suggestion",
                    "Enter suggestion here...",
                    isEditable
                      ? (nextValue) =>
                          updateReport((current) => ({
                            ...current,
                            suggestions: current.suggestions.map((suggestion) =>
                              suggestion.key === item.key ? { ...suggestion, body: nextValue } : suggestion
                            ),
                          }))
                      : undefined
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="pull-quote">
            <div className="pull-quote-text">{report.suggestionsQuote}</div>
            <div className="pull-quote-attr">- {report.suggestionsQuoteAuthor}</div>
          </div>
        </div>
        {renderPageFooter(3)}
      </div>
      )}

      {showAiSections && (
      <div className="coach-report-preview__page">
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderSectionHeader("Resources for Growth")}
          <p className="coach-report-preview__intro-text">
            The following resources were selected specifically for {report.coachName} based on the themes that emerged from this evaluation. Each one directly addresses an area of opportunity identified in the {terminology.respondentLower} and parent survey data.
          </p>
          {/* {isEditable && lastRemovedResource ? (
            <div className="coach-report-preview__resource-undo-row">
              <button
                className="coach-report-preview__resource-undo"
                onClick={undoRemoveResource}
                title="Restore the last removed resource"
                type="button"
              >
                Undo Remove
              </button>
            </div>
          ) : null} */}
          <div className="coach-report-preview__resource-list">
            {report.resources.map((item) => (
              <div
                className={`coach-report-preview__resource-card-big ${getResourceVariantClass(item.category)}`}
                key={item.key}
              >
                <div className="coach-report-preview__resource-card-top">
                  <div className="coach-report-preview__resource-type-badge">{item.category}</div>
                  {isEditable ? (
                    <div className="coach-report-preview__resource-card-actions">
                      <button
                        type="button"
                        className="coach-report-preview__resource-remove"
                        onClick={() => removeResource(item.key)}
                      >
                        <span className="coach-report-preview__resource-remove-icon">x</span>
                        <span className="coach-report-preview__resource-remove-label">Remove</span>
                      </button>
                    </div>
                  ) : null}
                </div>
                {isEditable ? (
                  renderEditableTextarea(
                    item.title,
                    COACH_REPORT_LIMITS.resourceTitle,
                    "coach-report-preview__editable-text coach-report-preview__editable-text--resource-title",
                    "Enter resource title here...",
                    (nextValue) =>
                      updateResource(item.key, (resource) => ({
                        ...resource,
                        title: nextValue,
                      }))
                  )
                ) : (
                  <div className="coach-report-preview__resource-title-big">{item.title}</div>
                )}
                {isEditable ? (
                  renderEditableTextarea(
                    item.body,
                    COACH_REPORT_LIMITS.resourceBody,
                    "coach-report-preview__editable-text coach-report-preview__editable-text--resource-body",
                    "Enter resource description here...",
                    (nextValue) =>
                      updateResource(item.key, (resource) => ({
                        ...resource,
                        body: nextValue,
                      }))
                  )
                ) : (
                  <div className="coach-report-preview__resource-desc">
                    {renderTextWithBoldScores(item.body)}
                  </div>
                )}
                <div className="coach-report-preview__resource-why-chosen">
                  <p className="coach-report-preview__resource-why-chosen-para">
                    <span className="coach-report-preview__resource-why-chosen-label">
                      Why chosen for {report.coachName}:
                    </span>{" "}
                    {isEditable ? (
                      <>
                        <span
                          className="coach-report-preview__resource-why-chosen-text coach-report-preview__editable-text coach-report-preview__editable-text--resource-why-chosen coach-report-preview__edit-field"
                          contentEditable={true}
                          suppressContentEditableWarning={true}
                          data-maxlength={COACH_REPORT_LIMITS.resourceWhyChosen}
                          data-placeholder="Enter why this resource was chosen..."
                          ref={(el: any) => {
                            const browserDocument: any =
                              typeof document !== "undefined" ? document : null;
                            if (
                              el &&
                              (!browserDocument ||
                                browserDocument.activeElement !== el) &&
                              String(el.innerText || "") !==
                                String(item.whyChosen || "")
                            ) {
                              el.innerText = item.whyChosen || "";
                            }
                          }}
                          onInput={(event: any) => {
                            const raw = String(
                              (event &&
                                event.currentTarget &&
                                event.currentTarget.innerText) ||
                                ""
                            );
                            const nextValue =
                              raw.length > COACH_REPORT_LIMITS.resourceWhyChosen
                                ? raw.slice(
                                    0,
                                    COACH_REPORT_LIMITS.resourceWhyChosen
                                  )
                                : raw;
                            if (
                              nextValue !== raw &&
                              event &&
                              event.currentTarget
                            ) {
                              event.currentTarget.innerText = nextValue;
                            }
                            updateResource(item.key, (resource) => ({
                              ...resource,
                              whyChosen: nextValue,
                            }));
                          }}
                        />
                        <span
                          aria-hidden="true"
                          className="coach-report-preview__resource-why-chosen-text coach-report-preview__editable-text coach-report-preview__editable-text--resource-why-chosen coach-report-preview__print-text"
                          onClick={(event: any) => {
                            const para: any =
                              event && event.currentTarget
                                ? event.currentTarget.parentNode
                                : null;
                            const editEl: any = para
                              ? para.querySelector(
                                  ".coach-report-preview__edit-field"
                                )
                              : null;
                            if (editEl && editEl.focus) {
                              editEl.focus();
                            }
                          }}
                        >
                          {renderTextWithBoldScores(item.whyChosen)}
                        </span>
                      </>
                    ) : (
                      <span className="coach-report-preview__resource-why-chosen-text">
                        {renderTextWithBoldScores(item.whyChosen)}
                      </span>
                    )}
                  </p>
                  {isEditable ? (
                    <div className="coach-report-preview__char-limit">
                      {String(item.whyChosen || "").length}/
                      {COACH_REPORT_LIMITS.resourceWhyChosen}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        {renderPageFooter(4)}
      </div>
      )}

      {showAiSections && (
      <div
        className={`coach-report-preview__page${
          showPrintOnlyFromModal
            ? " coach-report-preview__screen-only-from-modal"
            : ""
        }`}
      >
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderSectionHeader("Final Note")}
          <div className="coach-report-preview__final-note-box">
            <div className="coach-report-preview__final-note-title">
              A Message to {chartLabel}
            </div>
            {renderEditableTextarea(
              report.finalNote,
              report.finalNoteMaxLength,
              "coach-report-preview__editable-text coach-report-preview__editable-text--final-note",
              "Coach's personal commitments and goals for the upcoming season will be recorded here during the AD–coach meeting.",
              isEditable
                ? (nextValue) => updateReport((current) => ({ ...current, finalNote: nextValue }))
                : undefined
            )}
          </div>

        </div>
        {renderPageFooter(pageName === "reportmodal" ? 5 : 8)}
      </div>
      )}

      {showRawSections && (
      <div className={`coach-report-preview__page coach-report-preview__page--expectations ${showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}`}>
        {renderRunningHeader()}
        <div className="coach-report-preview__data-section-banner">
          <div className="coach-report-preview__data-section-banner-title">
            {isAdSurvey
              ? "Coach & AD Survey Data"
              : "Student & Teacher Survey Data"}
          </div>
          <div className="coach-report-preview__data-section-banner-sub">
            {"Detailed scoring by category — "}
            {terminology.respondentLower}
            {" average vs. "}
            {terminology.selfScoreLabel.toLowerCase()}
          </div>
        </div>
        <div className="coach-report-preview__page-content">
          <div className="coach-report-preview__score-legend">
            <div className="coach-report-preview__score-legend-item">
              <span className="coach-report-preview__score-legend-swatch coach-report-preview__score-legend-swatch--student" />
              {terminology.respondent} Average
            </div>
            <div className="coach-report-preview__score-legend-item">
              <span className="coach-report-preview__score-legend-swatch coach-report-preview__score-legend-swatch--coach" />
              {terminology.selfScoreLabel}
            </div>
          </div>
          {renderPairedComparisonSections(
            report.expectations,
            report.relationalWarmth
          )}
        </div>
        {renderPageFooter(5)}
      </div>
      )}

      {showRawSections && showAiSections && (
      <div className={`coach-report-preview__page coach-report-preview__page--fill ${showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}`}>
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content coach-report-preview__page-content--fill">
          <div className="coach-report-preview__wise-section">
            {renderSectionHeader("Wise Leadership Grid & Administrator Comments")}
            <div className="coach-report-preview__wise-layout">
            <div className="coach-report-preview__wise-grid-column">
              <div className="coach-report-preview__leader-panel coach-report-preview__leader-panel--chart">
                <div className="coach-report-preview__leader-grid">
                  <WiseLeadershipGridChart
                    warmth={warmthScore}
                    expectations={expectationsScore}
                    label={chartLabel}
                    accentColor={accentColor}
                    coachWarmth={coachWarmthScore}
                    coachExpectations={coachExpectationsScore}
                    isAdSurvey={Boolean(isAdSurvey)}
                  />
                </div>
              </div>
            </div>
            <div className="coach-report-preview__wise-side-column">
              <div className="coach-report-preview__grid-placement-card">
                <div className="coach-report-preview__grid-placement-label">
                  GRID PLACEMENT
                </div>
                <div className="coach-report-preview__grid-metric-row">
                  <span className="coach-report-preview__grid-metric-name">
                    Relational Warmth
                  </span>
                  <span
                    className="coach-report-preview__grid-metric-value"
                    style={{ color: accentColor }}
                  >
                    {warmthDisplay}
                  </span>
                </div>
                <div className="coach-report-preview__grid-metric-row">
                  <span className="coach-report-preview__grid-metric-name">
                    Expectations
                  </span>
                  <span
                    className="coach-report-preview__grid-metric-value"
                    style={{ color: accentColor }}
                  >
                    {expectationsDisplay}
                  </span>
                </div>
                <div
                  className="coach-report-preview__grid-quadrant-box"
                  style={{ background: placementTint }}
                >
                  <div className="coach-report-preview__grid-quadrant-title">
                    Quadrant: {quadrantTitle}
                  </div>
                  <p className="coach-report-preview__grid-quadrant-body">
                    {renderTextWithBoldScores(quadrantSummary)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>
          <div className="coach-report-preview__admin-comments-card coach-report-preview__admin-comments-card--fill">
            <div className="coach-report-preview__admin-comments-label">
              ADMINISTRATOR COMMENTS
            </div>
            <div className="coach-report-preview__admin-comments-body-wrap">
              {renderEditableTextarea(
                report.wiseLeadership.administratorComments,
                COACH_REPORT_LIMITS.administratorComments,
                "coach-report-preview__admin-comments-body",
                "Click to add administrator comments...",
                isEditable
                  ? (nextValue) =>
                      updateReport((current) => ({
                        ...current,
                        wiseLeadership: {
                          ...current.wiseLeadership,
                          administratorComments: nextValue,
                        },
                      }))
                  : undefined
              )}
            </div>
            {showPrintOnlyFromModal ? (
              <div className="coach-report-preview__admin-comments-signature">
                {(report.wiseLeadership.administratorSignature || "").trim()
                  ? report.wiseLeadership.administratorSignature
                  : "— JH, School Admin"}
              </div>
            ) : (
              renderEditableTextarea(
                report.wiseLeadership.administratorSignature,
                COACH_REPORT_LIMITS.administratorSignature,
                "coach-report-preview__admin-comments-signature",
                "Administrator signature",
                isEditable
                  ? (nextValue) =>
                      updateReport((current) => ({
                        ...current,
                        wiseLeadership: {
                          ...current.wiseLeadership,
                          administratorSignature: nextValue,
                        },
                      }))
                  : undefined
              )
            )}
          </div>
        </div>
        {renderPageFooter(4)}
      </div>
      )}

      {/* Print Data Only: Survey Data first; graph after (not on page 1). */}
      {showRawSections && !showAiSections && (
      <div className={`coach-report-preview__page coach-report-preview__page--fill ${showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}`}>
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content coach-report-preview__page-content--fill">
          <div className="coach-report-preview__wise-section">
            {renderSectionHeader("Wise Leadership Grid & Administrator Comments")}
            <div className="coach-report-preview__wise-layout">
            <div className="coach-report-preview__wise-grid-column">
              <div className="coach-report-preview__leader-panel coach-report-preview__leader-panel--chart">
                <div className="coach-report-preview__leader-grid">
                  <WiseLeadershipGridChart
                    warmth={warmthScore}
                    expectations={expectationsScore}
                    label={chartLabel}
                    accentColor={accentColor}
                    coachWarmth={coachWarmthScore}
                    coachExpectations={coachExpectationsScore}
                    isAdSurvey={Boolean(isAdSurvey)}
                  />
                </div>
              </div>
            </div>
            <div className="coach-report-preview__wise-side-column">
              <div className="coach-report-preview__grid-placement-card">
                <div className="coach-report-preview__grid-placement-label">
                  GRID PLACEMENT
                </div>
                <div className="coach-report-preview__grid-metric-row">
                  <span className="coach-report-preview__grid-metric-name">
                    Relational Warmth
                  </span>
                  <span
                    className="coach-report-preview__grid-metric-value"
                    style={{ color: accentColor }}
                  >
                    {warmthDisplay}
                  </span>
                </div>
                <div className="coach-report-preview__grid-metric-row">
                  <span className="coach-report-preview__grid-metric-name">
                    Expectations
                  </span>
                  <span
                    className="coach-report-preview__grid-metric-value"
                    style={{ color: accentColor }}
                  >
                    {expectationsDisplay}
                  </span>
                </div>
                <div
                  className="coach-report-preview__grid-quadrant-box"
                  style={{ background: placementTint }}
                >
                  <div className="coach-report-preview__grid-quadrant-title">
                    Quadrant: {quadrantTitle}
                  </div>
                  <p className="coach-report-preview__grid-quadrant-body">
                    {renderTextWithBoldScores(quadrantSummary)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>
          {/* <div className="coach-report-preview__admin-comments-card coach-report-preview__admin-comments-card--fill">
            <div className="coach-report-preview__admin-comments-label">
              ADMINISTRATOR COMMENTS
            </div>
            <div className="coach-report-preview__admin-comments-body-wrap">
              {renderEditableTextarea(
                report.wiseLeadership.administratorComments,
                COACH_REPORT_LIMITS.administratorComments,
                "coach-report-preview__admin-comments-body",
                "Click to add administrator comments...",
                isEditable
                  ? (nextValue) =>
                      updateReport((current) => ({
                        ...current,
                        wiseLeadership: {
                          ...current.wiseLeadership,
                          administratorComments: nextValue,
                        },
                      }))
                  : undefined
              )}
            </div>
            {renderEditableTextarea(
              report.wiseLeadership.administratorSignature,
              COACH_REPORT_LIMITS.administratorSignature,
              "coach-report-preview__admin-comments-signature",
              "Administrator signature",
              isEditable
                ? (nextValue) =>
                    updateReport((current) => ({
                      ...current,
                      wiseLeadership: {
                        ...current.wiseLeadership,
                        administratorSignature: nextValue,
                      },
                    }))
                : undefined
            )}
          </div> */}
        </div>
        {renderPageFooter(4)}
      </div>
      )}

      {showRawSections && (
      <div className={`coach-report-preview__page ${showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}`}>
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderCommentPairTables(
            rangeIndexes(
              Math.max(studentSections.length, coachSections.length)
            ).map((index) => {
              const studentSection = studentSections[index];
              const coachSection = coachSections[index];
              return {
                key:
                  (studentSection && studentSection.key) ||
                  (coachSection && coachSection.key) ||
                  `comment-pair-${index}`,
                leftQuestion: studentSection && studentSection.question,
                rightQuestion: coachSection && coachSection.question,
                rightQuestionClassName:
                  "coach-report-preview__comment-question coach-report-preview__comment-question--coach",
                leftBody: studentSection ? (
                  <div className="coach-report-preview__comment-items">
                    {studentSection.comments.map((item, itemIndex) =>
                      renderCommentItem(item, itemIndex)
                    )}
                  </div>
                ) : null,
                rightBody: coachSection ? (
                  <div className="coach-report-preview__comment-placeholder-box">
                    {hasCoachResponse(coachSection)
                      ? coachSection.placeholder
                      : terminology.responsePlaceholder}
                  </div>
                ) : null,
              };
            }),
            <div className="coach-report-preview__comment-summaries-headers">
              <div>{renderSectionHeader(`${terminology.respondent} Comment Summaries`)}</div>
              <div>{renderSectionHeader(terminology.commentSummaries)}</div>
            </div>
          )}
        </div>
        {renderPageFooter(6)}
      </div>
      )}

      {showRawSections && (
      <div className={`coach-report-preview__page ${showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}`}>
        {renderRunningHeader()}
        <div className="coach-report-preview__page-content">
          {renderSectionHeader("Parent Feedback")}
          <div className="coach-report-preview__data-table-wrap coach-report-preview__data-table-wrap--parent-feedback">
            <table className="coach-report-preview__data-table coach-report-preview__data-table--parent-feedback">
              <thead>
                <tr>
                  <th>Parent Survey Item</th>
                  <th className="coach-report-preview__score-heading">Score</th>
                </tr>
              </thead>
              <tbody>
                {report.parentFeedback.rows.map((item) => (
                  <tr key={item.key}>
                    <td>{item.label}</td>
                    <td className="coach-report-preview__score-col">
                      {renderTableScoreBar(item.score)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Parent Average</strong>
                  </td>
                  <td className="coach-report-preview__score-col">
                    {renderTableScoreBar(report.parentFeedback.average, {
                      showBar: false,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {showPrintOnlyFromModal ? (
            <div className="coach-report-preview__summary-box">
              <div className="coach-report-preview__summary-box-label">
                Parent Feedback Summary
              </div>
              <div className="coach-report-preview__editable-text coach-report-preview__editable-text--parent-summary">
                {renderTextWithBoldScores(report.parentFeedback.summary)}
              </div>
            </div>
          ) : null}

          {renderCommentPairTables(
              rangeIndexes(
                Math.max(leftParentSections.length, rightParentSections.length)
              ).map((index) => {
                const leftSection = leftParentSections[index];
                const rightSection = rightParentSections[index];
                return {
                  key:
                    (leftSection && leftSection.key) ||
                    (rightSection && rightSection.key) ||
                    `parent-comment-pair-${index}`,
                  leftQuestion: leftSection && leftSection.question,
                  rightQuestion: rightSection && rightSection.question,
                  leftBody: leftSection ? (
                    <div className="coach-report-preview__comment-items">
                      {leftSection.comments.map((item, itemIndex) =>
                        renderCommentItem(item, itemIndex)
                      )}
                    </div>
                  ) : null,
                  rightBody: rightSection ? (
                    <div className="coach-report-preview__comment-items">
                      {rightSection.comments.map((item, itemIndex) =>
                        renderCommentItem(item, itemIndex)
                      )}
                    </div>
                  ) : null,
                };
              }),
              renderSectionHeader("Parent Comment Summaries")
            )}
        </div>
        {renderPageFooter(7)}
      </div>
      )}

      {showAiSections && (
      <div className={showPrintOnlyFromModal ? "coach-report-preview__print-only-from-modal" : ""}>
        <div className="coach-report-preview__page">
          {renderRunningHeader()}
          <div className="coach-report-preview__page-content">
            {showPrintOnlyFromModal ? (
              <>
                {renderSectionHeader("Final Note")}
                <div className="coach-report-preview__final-note-box">
                  <div className="coach-report-preview__final-note-title">
                    A Message to {chartLabel}
                  </div>
                  <div className="coach-report-preview__editable-text coach-report-preview__editable-text--final-note">
                    {renderTextWithBoldScores(report.finalNote)}
                  </div>
                </div>
              </>
            ) : null}
            {renderSectionHeader(terminology.commitments)}
            <p className="coach-report-preview__leader-commitments-intro">{leaderCommitmentsIntroText}</p>
            <div className="coach-report-preview__commitment-box">
              {renderEditableTextarea(
                report.leaderCommitments,
                report.leaderCommitmentsMaxLength,
                "coach-report-preview__editable-text coach-report-preview__editable-text--commitments",
                terminology.commitmentsPlaceholder,
                isEditable
                  ? (nextValue) => updateReport((current) => ({ ...current, leaderCommitments: nextValue }))
                  : undefined
                )}
            </div>
          </div>
          {renderPageFooter(9)}
        </div>
      </div>
      )}
    </div>
  );
};

export default CoachReportPreview;










// WEBPACK FOOTER //
// ./src/pages/director/CoachReportPreview/CoachReportPreview.tsx